import firebase from 'firebase/app'
import 'firebase/firestore'
import format from 'date-fns/format'
import subDays from 'date-fns/sub_days'
import subMonths from 'date-fns/sub_months'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'
import { apiNodeRequest } from './core/main'
import {
  addDebiComponentValue,
  calculateDebiBehaviorBreakdown,
  DebiBehaviorBreakdown,
  DebiComponentTotals,
} from './utils/debiScore'
import { pgEngagementIndexDay } from './engagementPgApi'

export const WHO5_QUESTION_IDS = [
  '-MdMv_XgThL0o2iunlhE',
  '-MdMw8kfpZtxnppGUA1f',
  '-MdMyavdF-YvuyzW6cA5',
  '-MdMz-TodEXbj3AC68oP',
  '-MdMzwfySVTjhOAGoxTP',
]
const HAPPINESS_QUESTION_ID = '-Kgs61AHOnpJ3rfyQr6t'
const PEOPLE_INSIGHT_API_TIMEOUT_MS = 60000

function roundMetric(value: number): number {
  return Math.round(value * 10) / 10
}

function clampPercent(value: number): number {
  if (isNaN(value)) return 0
  return roundMetric(Math.max(0, Math.min(value, 100)))
}

function daysBetween(fromDateKey: string, toDateKey: string): number {
  if (!fromDateKey || !toDateKey) return 0
  const from = new Date(fromDateKey + 'T00:00:00').getTime()
  const to = new Date(toDateKey + 'T00:00:00').getTime()
  if (isNaN(from) || isNaN(to)) return 0
  return Math.max(0, Math.floor((to - from) / 86400000))
}

function normalizeDateKey(value: any): string {
  if (!value) return ''
  if (typeof value === 'string') return value.slice(0, 10)

  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value)
  if (isNaN(date.getTime())) return ''

  return format(date, 'YYYY-MM-DD')
}

function getQuestionUserEmail(data: any): string {
  return data.users || data.email || ''
}

function parseAnswerValue(value: any): number | null {
  if (value === null || value === undefined || value === '') return null

  const parsed = Number(value)
  if (isNaN(parsed)) return null

  return parsed
}

function getDailyCheckinColor(value: any): string {
  const answer = parseAnswerValue(value)
  if (answer === null) return ''
  if (answer === 0 || answer === 1) return 'green'
  if (answer === 2) return 'yellow'
  if (answer === 3 || answer === 4) return 'red'
  return 'gray'
}

function setDailyAnswerColor(
  dailyAnswerColorByEmail: Record<string, Record<string, string>>,
  email: string,
  dateKey: string,
  color: string,
  startDate: string,
  endDate: string
) {
  if (!email || !dateKey || !color) return
  if (dateKey < startDate || dateKey > endDate) return
  if (!dailyAnswerColorByEmail[email]) {
    dailyAnswerColorByEmail[email] = {}
  }
  dailyAnswerColorByEmail[email][dateKey] = color
}

function addDailyExpectedDate(
  dailyExpectedDatesByEmail: Record<string, Set<string>>,
  email: string,
  dateKey: string,
  startDate: string,
  endDate: string
) {
  if (!email || !dateKey) return
  if (dateKey < startDate || dateKey > endDate) return
  if (!dailyExpectedDatesByEmail[email]) {
    dailyExpectedDatesByEmail[email] = new Set<string>()
  }
  dailyExpectedDatesByEmail[email].add(dateKey)
}

function ingestDailyCheckinQuestions(
  snapshot: any,
  dailyAnswerColorByEmail: Record<string, Record<string, string>>,
  dailyExpectedDatesByEmail: Record<string, Set<string>>,
  startDate: string,
  endDate: string
) {
  if (!snapshot) return

  snapshot.forEach((doc: any) => {
    const data = doc.data()
    const email = getQuestionUserEmail(data)
    const dateKey = normalizeDateKey(data.date)
    if (!email || !dateKey) return

    addDailyExpectedDate(dailyExpectedDatesByEmail, email, dateKey, startDate, endDate)

    const color = getDailyCheckinColor(data.answer)
    if (color) {
      setDailyAnswerColor(dailyAnswerColorByEmail, email, dateKey, color, startDate, endDate)
    }
  })
}

function getPreviousQuarterStartDate(date: Date): string {
  const month = date.getMonth()
  const quarterStartMonth = Math.floor(month / 3) * 3
  return format(new Date(date.getFullYear(), quarterStartMonth - 3, 1), 'YYYY-MM-DD')
}

export function getQuarterCacheKey(company: string, date: Date): string {
  const quarter = Math.floor(date.getMonth() / 3) + 1
  return `${company}|${date.getFullYear()}|Q${quarter}`
}

function normalizeWho5ScoreValue(value: any): number | null {
  const parsed = parseAnswerValue(value)
  if (parsed === null) return null

  if (parsed >= 0 && parsed <= 5) return 5 - parsed
  return parsed
}

function isExpectedWorkday(dateKey: string, holidayDates: Set<string>): boolean {
  if (!dateKey || holidayDates.has(dateKey)) return false

  const date = new Date(dateKey + 'T00:00:00')
  if (isNaN(date.getTime())) return false

  const day = date.getDay()
  return day !== 0 && day !== 6
}

function countExpectedWorkdays(startDate: string, endDate: string, holidayDates: Set<string>): number {
  if (!startDate || !endDate || startDate > endDate) return 0

  const cursor = new Date(startDate + 'T00:00:00')
  const end = new Date(endDate + 'T00:00:00')
  let count = 0

  while (cursor.getTime() <= end.getTime()) {
    const dateKey = format(cursor, 'YYYY-MM-DD')
    if (isExpectedWorkday(dateKey, holidayDates)) {
      count++
    }
    cursor.setDate(cursor.getDate() + 1)
  }

  return count
}

function countDatesInRange(dates: Set<string> | undefined, startDate: string, endDate: string): number {
  if (!dates || !startDate || !endDate || startDate > endDate) return 0

  let count = 0
  dates.forEach((dateKey) => {
    if (dateKey >= startDate && dateKey <= endDate) {
      count++
    }
  })

  return count
}

function countAnsweredCheckinDates(
  answerDates: string[],
  startDate: string,
  endDate: string,
  holidayDates: Set<string>,
  expectedDates?: Set<string>
): number {
  let count = 0
  answerDates.forEach((dateKey) => {
    if (dateKey < startDate || dateKey > endDate) return
    if (expectedDates) {
      if (expectedDates.has(dateKey)) count++
      return
    }
    if (isExpectedWorkday(dateKey, holidayDates)) count++
  })
  return count
}

function isNextExpectedCheckinDate(previousDateKey: string, dateKey: string, holidayDates?: Set<string>): boolean {
  if (!previousDateKey || !dateKey || previousDateKey >= dateKey) return false

  const cursor = new Date(previousDateKey + 'T00:00:00')
  const end = new Date(dateKey + 'T00:00:00')
  if (isNaN(cursor.getTime()) || isNaN(end.getTime())) return false

  cursor.setDate(cursor.getDate() + 1)
  while (cursor.getTime() <= end.getTime()) {
    const cursorKey = format(cursor, 'YYYY-MM-DD')
    if (!holidayDates || isExpectedWorkday(cursorKey, holidayDates)) {
      return cursorKey === dateKey
    }
    cursor.setDate(cursor.getDate() + 1)
  }

  return false
}

function getWellbeingRiskSignal(employee: any): number {
  if (!employee || employee.healthbar === undefined || employee.healthbar === null || employee.healthbar === '') return 0

  const wellbeing = Number(employee.healthbar)
  if (isNaN(wellbeing)) return 0
  if (wellbeing < 30) return 1
  if (wellbeing < 50) return 0.5
  return 0
}

function getConnectionRiskSignal(employee: any): number {
  const networkType = String((employee && employee.network_type) || '').toLowerCase()
  if (!networkType) return 0
  if (networkType.indexOf('low') >= 0) return 1
  if (networkType.indexOf('medium') >= 0) return 0.5
  return 0
}

export function calculateFlightRiskScore(
  dailyAnswers: Record<string, string>,
  currentDate: string,
  employee?: any,
  holidayDates?: Set<string>
): number | null {
  const answerMap = dailyAnswers || {}
  const dailyAnswerDates = Object.keys(answerMap)
    .filter((dateKey) => ['green', 'yellow', 'red'].indexOf(String(answerMap[dateKey]).toLowerCase()) >= 0)
    .sort()

  if (dailyAnswerDates.length === 0) return null

  const RECENCY_HALF_LIFE_DAYS = 30
  const SATURATION_RATE = 0.25
  const NOISE_FLOOR_RATE = 0.03
  const MIN_REDS_FOR_CHRONIC = 3
  const MIN_STREAK_FOR_ACUTE = 3
  const MAX_STREAK_DAYS = 5
  const STREAK_RECENCY_DAYS = 45
  const FULL_CONFIDENCE_RESPONSES = 10
  const CONTEXT_ONLY_CAP = 0.35

  let redWeightSum = 0
  let weightSum = 0
  let redCount = 0
  let longestRecentStreak = 0
  let currentStreak = 0
  let previousRedDate = ''

  for (let d = 0; d < dailyAnswerDates.length; d++) {
    const dateKey = dailyAnswerDates[d]
    const age = daysBetween(dateKey, currentDate)
    const recency = Math.exp(-age / RECENCY_HALF_LIFE_DAYS)
    const isRed = String(answerMap[dateKey]).toLowerCase() === 'red'

    weightSum += recency
    if (isRed) {
      redWeightSum += recency
      redCount++
      currentStreak = previousRedDate && isNextExpectedCheckinDate(previousRedDate, dateKey, holidayDates)
        ? currentStreak + 1
        : 1
      previousRedDate = dateKey
      if (age <= STREAK_RECENCY_DAYS && currentStreak > longestRecentStreak) {
        longestRecentStreak = currentStreak
      }
    } else {
      currentStreak = 0
      previousRedDate = ''
    }
  }

  let chronicSignal = 0
  if (redCount >= MIN_REDS_FOR_CHRONIC && weightSum > 0) {
    const weightedRedRate = redWeightSum / weightSum
    const adjustedRate = Math.max(0, weightedRedRate - NOISE_FLOOR_RATE)
    chronicSignal = Math.min(1, Math.sqrt(adjustedRate / (SATURATION_RATE - NOISE_FLOOR_RATE)))
  }

  let acuteSignal = 0
  if (longestRecentStreak >= MIN_STREAK_FOR_ACUTE) {
    acuteSignal = Math.min(1, (longestRecentStreak - (MIN_STREAK_FOR_ACUTE - 1)) / (MAX_STREAK_DAYS - (MIN_STREAK_FOR_ACUTE - 1)))
  }

  const signalHigh = Math.max(chronicSignal, acuteSignal)
  const signalLow = Math.min(chronicSignal, acuteSignal)
  const dailySignal = signalHigh * 0.8 + signalLow * 0.2
  const wellbeingSignal = getWellbeingRiskSignal(employee)
  const connectionSignal = getConnectionRiskSignal(employee)
  const contextSignal = Math.max(wellbeingSignal, connectionSignal)
  const pairedContextSignal = wellbeingSignal > 0 && connectionSignal > 0
    ? (wellbeingSignal * 0.6) + (connectionSignal * 0.4)
    : 0

  let combined = dailySignal
  if (dailySignal > 0 && contextSignal > 0) {
    combined = Math.min(1, dailySignal + ((1 - dailySignal) * contextSignal * 0.25))
  } else if (redCount > 0 && contextSignal > 0) {
    combined = Math.min(CONTEXT_ONLY_CAP, contextSignal * CONTEXT_ONLY_CAP)
  } else if (pairedContextSignal > 0) {
    combined = Math.min(CONTEXT_ONLY_CAP, pairedContextSignal * CONTEXT_ONLY_CAP)
  }

  const confidence = Math.min(Math.sqrt(dailyAnswerDates.length / FULL_CONFIDENCE_RESPONSES), 1)
  return roundMetric(combined * confidence * 100)
}

function calculateWho5Score(answerSet: Record<string, number>): number {
  let dataSum = 0
  let maximum = 0

  for (let i = 0; i < WHO5_QUESTION_IDS.length; i++) {
    const answer = Number(answerSet[WHO5_QUESTION_IDS[i]])
    if (isNaN(answer)) return 0

    dataSum += answer
    maximum += 5
  }

  if (maximum === 0) return 0
  return roundMetric((dataSum * 100) / maximum)
}

export function buildWho5ScoresFromSnapshots(snapshots: any[]): Record<string, number> {
  const who5AnswersByEmail: Record<string, Record<string, Record<string, number>>> = {}

  snapshots.forEach((snapshot: any) => {
    if (!snapshot) return

    snapshot.forEach((doc: any) => {
      const data = doc.data()
      const email = getQuestionUserEmail(data)
      const dateKey = normalizeDateKey(data.date)
      const answer = normalizeWho5ScoreValue(data.answer)
      if (!email || !dateKey || answer === null || !data.quest_id) return

      if (!who5AnswersByEmail[email]) who5AnswersByEmail[email] = {}
      if (!who5AnswersByEmail[email][dateKey]) who5AnswersByEmail[email][dateKey] = {}

      who5AnswersByEmail[email][dateKey][data.quest_id] = answer
    })
  })

  const who5ByEmail: Record<string, number> = {}
  Object.keys(who5AnswersByEmail).forEach((email) => {
    const dates = Object.keys(who5AnswersByEmail[email]).sort().reverse()

    for (let i = 0; i < dates.length; i++) {
      const answerSet = who5AnswersByEmail[email][dates[i]]
      const hasCompleteSet = WHO5_QUESTION_IDS.every((questionId) => answerSet[questionId] !== undefined)

      if (hasCompleteSet) {
        who5ByEmail[email] = calculateWho5Score(answerSet)
        break
      }
    }
  })

  return who5ByEmail
}

function listMatchesCompany(list: any, company: string): boolean {
  if (!Array.isArray(list) || list.length === 0) return false

  const firstRecord = list.find((item: any) => !!item && !item.placeholder)
  if (!firstRecord || !firstRecord.company) return true

  return firstRecord.company === company
}

const PEOPLE_TABLE_SCORE_KEYS = [
  'computed_engagement',
  'computed_performance',
  'computed_peer_trust',
  'computed_trust',
]

const PEOPLE_WELLBEING_SCORE_KEYS = [
  'computed_who5',
]

const PEOPLE_PRIMARY_CACHE_VERSION = 'v3'
const PEOPLE_PRIMARY_CACHE_PREFIX = 'happily.people.primaryMetrics'
const PEOPLE_PRIMARY_VISIBLE_LIMIT = 25
const EMAIL_QUERY_CHUNK_SIZE = 10
const PEOPLE_ENGAGEMENT_WINDOW_DAYS = 30

const PEOPLE_PRIMARY_ENGAGEMENT_FIELDS = [
  'computed_engagement',
  'computed_engagement_source',
  'computed_engagement_period',
  'computed_engagement_breakdown',
]

const PEOPLE_PRIMARY_RELATIONSHIP_FIELDS = [
  'computed_performance',
  'computed_exceeding_performance',
  'computed_peer_trust',
  'computed_trust',
  'computed_giving_recognition',
  'computed_receiving_recognition',
]

const PEOPLE_PRIMARY_WHO5_FIELDS = [
  'computed_who5',
]

function mergeScoresByEmail(existingScores: Record<string, any>, incomingScores: Record<string, any>): Record<string, any> {
  const merged: Record<string, any> = Object.assign({}, existingScores || {})

  Object.keys(incomingScores || {}).forEach((email) => {
    merged[email] = Object.assign({}, merged[email] || {}, incomingScores[email] || {})
  })

  return merged
}

function hasScoreFieldsForEmployees(scores: Record<string, any>, employees: any[], fields: string[]): boolean {
  const list = Array.isArray(employees) ? employees.filter((item: any) => item && item.email) : []
  if (list.length === 0) return false

  return list.every((employee: any) => {
    const employeeScores = scores && scores[employee.email]
    if (!employeeScores) return false

    return fields.every((field) => employeeScores[field] !== undefined)
  })
}

function uniqueEmployeesByEmail(employees: any[]): any[] {
  const result: any[] = []
  const seen: Record<string, boolean> = {}

  ;(Array.isArray(employees) ? employees : []).forEach((employee: any) => {
    if (!employee || !employee.email || seen[employee.email]) return
    seen[employee.email] = true
    result.push(employee)
  })

  return result
}

function getEmployeeEmails(employees: any[]): string[] {
  return uniqueEmployeesByEmail(employees).map((employee: any) => employee.email)
}

function chunkValues<T>(values: T[], chunkSize: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < values.length; i += chunkSize) {
    chunks.push(values.slice(i, i + chunkSize))
  }
  return chunks
}

function dedupeRecords(records: any[]): any[] {
  const result: any[] = []
  const seen: Record<string, boolean> = {}

  ;(records || []).forEach((record: any, index: number) => {
    if (!record) return
    const key = record.id || [
      record.email || '',
      record.email_to || '',
      record.sender_email || '',
      record.send_to_email || '',
      record.receiver || '',
      record.date || '',
      record.createdate || '',
      index,
    ].join('|')
    if (seen[key]) return
    seen[key] = true
    result.push(record)
  })

  return result
}

function snapshotToRecords(snapshot: any): any[] {
  const records: any[] = []
  if (!snapshot) return records

  snapshot.forEach((doc: any) => {
    const data = doc.data()
    if (doc.id && data.id === undefined) {
      data.id = doc.id
    }
    records.push(data)
  })

  return records
}

function safeCompanyKey(company: string): string {
  return encodeURIComponent(company || '')
}

export function getPeoplePrimaryMetricCacheKeys(company: string, date: Date) {
  const currentDate = format(date, 'YYYY-MM-DD')
  const past180 = format(subDays(date, 180), 'YYYY-MM-DD')
  const engagementEndDate = format(subDays(date, 1), 'YYYY-MM-DD')
  const engagementStartDate = format(subDays(date, PEOPLE_ENGAGEMENT_WINDOW_DAYS), 'YYYY-MM-DD')
  const base = `${PEOPLE_PRIMARY_CACHE_PREFIX}:${PEOPLE_PRIMARY_CACHE_VERSION}:${safeCompanyKey(company)}`

  return {
    engagement: `${base}:engagement-debi:${engagementStartDate}:${engagementEndDate}`,
    relationship: `${base}:relationship:${past180}:${currentDate}`,
    who5: `${base}:who5:${getQuarterCacheKey(company, date)}`,
  }
}

function getBrowserStorage(): Storage | null {
  if (typeof window === 'undefined' || !window.localStorage) return null

  try {
    const testKey = `${PEOPLE_PRIMARY_CACHE_PREFIX}:test`
    window.localStorage.setItem(testKey, '1')
    window.localStorage.removeItem(testKey)
    return window.localStorage
  } catch (error) {
    return null
  }
}

function filterScoresForCache(scores: Record<string, any>, fields: string[]): Record<string, any> {
  const filtered: Record<string, any> = {}

  Object.keys(scores || {}).forEach((email) => {
    const source = scores[email] || {}
    const item: Record<string, any> = {}
    fields.forEach((field) => {
      if (source[field] !== undefined) {
        item[field] = source[field]
      }
    })
    if (Object.keys(item).length > 0) {
      filtered[email] = item
    }
  })

  return filtered
}

function omitScoreFields(scores: Record<string, any>, fields: string[]): Record<string, any> {
  const omitted: Record<string, any> = {}
  Object.keys(scores || {}).forEach((email) => {
    omitted[email] = Object.assign({}, scores[email])
    fields.forEach((field) => {
      delete omitted[email][field]
    })
    if (Object.keys(omitted[email]).length === 0) {
      delete omitted[email]
    }
  })
  return omitted
}

function readScoresCache(cacheKey: string): Record<string, any> {
  const storage = getBrowserStorage()
  if (!storage) return {}

  try {
    const raw = storage.getItem(cacheKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== PEOPLE_PRIMARY_CACHE_VERSION || !parsed.scores) return {}
    return parsed.scores
  } catch (error) {
    return {}
  }
}

function writeScoresCache(cacheKey: string, scores: Record<string, any>) {
  const storage = getBrowserStorage()
  if (!storage || !scores || Object.keys(scores).length === 0) return

  try {
    storage.setItem(cacheKey, JSON.stringify({
      version: PEOPLE_PRIMARY_CACHE_VERSION,
      createdAt: Date.now(),
      scores,
    }))
  } catch (error) {
    // Cache writes should never affect the page.
  }
}

function readPeoplePrimaryMetricCache(company: string, date: Date): Record<string, any> {
  const keys = getPeoplePrimaryMetricCacheKeys(company, date)
  return mergeScoresByEmail(
    mergeScoresByEmail(readScoresCache(keys.engagement), readScoresCache(keys.relationship)),
    readScoresCache(keys.who5)
  )
}

function writePeoplePrimaryMetricCaches(company: string, date: Date, scores: Record<string, any>) {
  const keys = getPeoplePrimaryMetricCacheKeys(company, date)
  writeScoresCache(keys.engagement, filterScoresForCache(scores, PEOPLE_PRIMARY_ENGAGEMENT_FIELDS))
  writeScoresCache(keys.relationship, filterScoresForCache(scores, PEOPLE_PRIMARY_RELATIONSHIP_FIELDS))
  writeScoresCache(keys.who5, filterScoresForCache(scores, PEOPLE_PRIMARY_WHO5_FIELDS))
}

function colorScore(c: string) {
  if (c === 'green') return 1.0
  if (c === 'yellow') return 0.5
  return 0
}

function colorPercent(c: string): number {
  return colorScore(c) * 100
}

function getEngagementComponentPercent(data: any, valueKey: string, colorKey: string): number {
  if (!data) return 0

  const parsed = parseAnswerValue(data[valueKey])
  if (parsed !== null) {
    return clampPercent(parsed)
  }

  return colorPercent(data[colorKey])
}

function addPercentComponent(
  components: DebiComponentTotals,
  type: string,
  percent: number,
  denominatorPercent = 100
) {
  addDebiComponentValue(
    components,
    type,
    (percent * denominatorPercent) / 100,
    denominatorPercent
  )
}

type EngagementScoreSource = 'debi' | 'monthly'

type EngagementScoreDetail = {
  computed_engagement: number | null;
  computed_engagement_source: EngagementScoreSource;
  computed_engagement_period: string;
  computed_engagement_breakdown: DebiBehaviorBreakdown;
}

function buildEngagementScoreDetail(
  components: DebiComponentTotals,
  source: EngagementScoreSource,
  period = ''
): EngagementScoreDetail {
  const breakdown = calculateDebiBehaviorBreakdown(components)
  return {
    computed_engagement: breakdown.score,
    computed_engagement_source: source,
    computed_engagement_period: period,
    computed_engagement_breakdown: breakdown,
  }
}

function buildMonthlyEngagementScoreDetail(data: any): EngagementScoreDetail | null {
  if (!data) return null

  const daily = getEngagementComponentPercent(data, 'daily', 'daily_color')
  const feedback = getEngagementComponentPercent(data, 'feedback', 'feedback_color')
  const recognition = getEngagementComponentPercent(data, 'recognition', 'recognition_color')
  const feedbackEffort = getEngagementComponentPercent(data, 'super_feedback', 'super_feedback_color')
  const valuesAlignment = getEngagementComponentPercent(data, 'super_recognition', 'super_recognition_color')
  const components: DebiComponentTotals = {}

  addPercentComponent(components, 'DPSRRS', daily)
  addPercentComponent(components, 'OFRS', feedback)
  addPercentComponent(components, 'OFES', feedbackEffort, feedback)
  addPercentComponent(components, 'RPRS', recognition)
  addPercentComponent(components, 'VAS', valuesAlignment, recognition)

  return buildEngagementScoreDetail(components, 'monthly', data.monthyear || '')
}

export function buildIndividualDebiEngagementScoreDetails(records: any[]): Record<string, EngagementScoreDetail> {
  const componentsByEmail: Record<string, DebiComponentTotals> = {}
  const periodByEmail: Record<string, { start: string; end: string }> = {}
  const explicitPeriodByEmail: Record<string, string> = {}

  ;(Array.isArray(records) ? records : []).forEach((record: any) => {
    if (!record || !record.email || !record.type) return
    if (!componentsByEmail[record.email]) {
      componentsByEmail[record.email] = {}
    }
    addDebiComponentValue(componentsByEmail[record.email], record.type, record.answer, record.total)

    if (record._engagementPeriod || record.engagementPeriod) {
      explicitPeriodByEmail[record.email] = record._engagementPeriod || record.engagementPeriod
    }

    const dateKey = normalizeDateKey(record.date)
    if (dateKey) {
      if (!periodByEmail[record.email]) {
        periodByEmail[record.email] = { start: dateKey, end: dateKey }
      } else {
        periodByEmail[record.email].start = dateKey < periodByEmail[record.email].start
          ? dateKey
          : periodByEmail[record.email].start
        periodByEmail[record.email].end = dateKey > periodByEmail[record.email].end
          ? dateKey
          : periodByEmail[record.email].end
      }
    }
  })

  const scores: Record<string, EngagementScoreDetail> = {}
  Object.keys(componentsByEmail).forEach((email) => {
    const period = periodByEmail[email]
    scores[email] = buildEngagementScoreDetail(
      componentsByEmail[email],
      'debi',
      explicitPeriodByEmail[email] || (period ? `${period.start} to ${period.end}` : '')
    )
  })

  return scores
}

export function buildIndividualDebiEngagementScores(records: any[]): Record<string, number | null> {
  const details = buildIndividualDebiEngagementScoreDetails(records)
  const scores: Record<string, number | null> = {}
  Object.keys(details).forEach((email) => {
    scores[email] = details[email].computed_engagement
  })

  return scores
}

function getMedian(arr: number[]): number {
  if (arr.length === 0) return 1
  const nonZero = arr.filter(v => v > 0).sort((a, b) => a - b)
  if (nonZero.length === 0) return 1
  const mid = Math.floor(nonZero.length / 2)
  return nonZero.length % 2 === 0
    ? (nonZero[mid - 1] + nonZero[mid]) / 2
    : nonZero[mid]
}

export function buildPeoplePrimaryMetricScores(
  employees: any[],
  monthlyRecords: any[],
  reviewRecords: any[],
  hereRecords: any[],
  feedbackRecords: any[],
  who5ByEmail: Record<string, number | null> = {},
  includeWho5 = false,
  debiEngagementRecords: any[] | null = null
): Record<string, any> {
  const scores: Record<string, any> = {}
  const employeeList = uniqueEmployeesByEmail(employees)
  const engagementByEmail: Record<string, any> = {}
  const hasDebiEngagementRecords = Array.isArray(debiEngagementRecords)
  const debiEngagementByEmail = hasDebiEngagementRecords
    ? buildIndividualDebiEngagementScoreDetails(debiEngagementRecords || [])
    : {}

  ;(monthlyRecords || []).forEach((data: any) => {
    if (!data || !data.email) return
    if (!engagementByEmail[data.email] || data.monthyear > engagementByEmail[data.email]._monthyear) {
      engagementByEmail[data.email] = {
        _monthyear: data.monthyear,
        daily_color: data.daily_color || '',
        daily: data.daily,
        feedback_color: data.feedback_color || '',
        feedback: data.feedback,
        recognition_color: data.recognition_color || '',
        recognition: data.recognition,
        super_feedback_color: data.super_feedback_color || '',
        super_feedback: data.super_feedback,
        super_recognition_color: data.super_recognition_color || '',
        super_recognition: data.super_recognition,
        peerfeedback_color: data.peerfeedback_color || '',
        peerfeedback: data.peerfeedback,
      }
    }
  })

  const perfByEmail: Record<string, { gems: number, exceedings: number, total: number }> = {}
  ;(reviewRecords || []).forEach((data: any) => {
    if (!data || !data.receiver || isNaN(data.culture) || isNaN(data.goal)) return
    const email = data.receiver
    if (!perfByEmail[email]) perfByEmail[email] = { gems: 0, exceedings: 0, total: 0 }
    perfByEmail[email].total++
    if ((data.culture == 3 || data.culture == 4) && (data.goal == 3 || data.goal == 4)) {
      perfByEmail[email].gems++
    }
    if (data.culture == 4 && data.goal == 4) {
      perfByEmail[email].exceedings++
    }
  })

  const recognitionByEmail: Record<string, { given: number, received: number }> = {}
  const alignmentByEmail: Record<string, { effortReceived: number, totalReceived: number }> = {}
  const recognitionSentTo: Record<string, Set<string>> = {}
  const recognitionReceivedFrom: Record<string, Set<string>> = {}
  ;(hereRecords || []).forEach((data: any) => {
    if (!data) return
    const sender = data.sender_email
    const receiver = data.send_to_email
    if (sender) {
      if (!recognitionByEmail[sender]) recognitionByEmail[sender] = { given: 0, received: 0 }
      recognitionByEmail[sender].given++
      if (receiver) {
        if (!recognitionSentTo[sender]) recognitionSentTo[sender] = new Set()
        recognitionSentTo[sender].add(receiver)
      }
    }
    if (receiver) {
      if (!recognitionByEmail[receiver]) recognitionByEmail[receiver] = { given: 0, received: 0 }
      recognitionByEmail[receiver].received++
      if (sender) {
        if (!recognitionReceivedFrom[receiver]) recognitionReceivedFrom[receiver] = new Set()
        recognitionReceivedFrom[receiver].add(sender)
      }
    }
    if (data.core_value_ai && receiver) {
      let maxRating = 0
      for (const key in data.core_value_ai) {
        const r = parseFloat(data.core_value_ai[key].rating)
        if (r > maxRating) maxRating = r
      }
      if (!alignmentByEmail[receiver]) alignmentByEmail[receiver] = { effortReceived: 0, totalReceived: 0 }
      alignmentByEmail[receiver].effortReceived += maxRating
      alignmentByEmail[receiver].totalReceived++
    }
  })

  const feedbackGivenByEmail: Record<string, number> = {}
  const trulyReceivedByEmail: Record<string, number> = {}
  const feedbackSentTo: Record<string, Set<string>> = {}
  const feedbackReceivedFrom: Record<string, Set<string>> = {}
  ;(feedbackRecords || []).forEach((data: any) => {
    if (!data) return
    const sender = data.email
    const receiver = data.email_to
    if (sender) {
      feedbackGivenByEmail[sender] = (feedbackGivenByEmail[sender] || 0) + 1
      if (receiver) {
        if (!feedbackSentTo[sender]) feedbackSentTo[sender] = new Set()
        feedbackSentTo[sender].add(receiver)
      }
    }
    if (receiver) {
      if (!feedbackReceivedFrom[receiver]) feedbackReceivedFrom[receiver] = new Set()
      if (sender) feedbackReceivedFrom[receiver].add(sender)
    }
    if (receiver && data.suggest && data.suggest.line1_en) {
      const rating = data.suggest.line1_en
      if (rating === 'Truly' || rating === 'Exceeding') {
        trulyReceivedByEmail[receiver] = (trulyReceivedByEmail[receiver] || 0) + 1
      }
    }
  })

  const allGivenCounts: number[] = []
  const allReceivedCounts: number[] = []

  for (let i = 0; i < employeeList.length; i++) {
    const emp = employeeList[i]
    if (!emp.email) continue
    const emailScores: any = {}
    const engData = engagementByEmail[emp.email]

    if (hasDebiEngagementRecords) {
      if (debiEngagementByEmail[emp.email]) {
        Object.assign(emailScores, debiEngagementByEmail[emp.email])
      } else {
        emailScores.computed_engagement = null
        emailScores.computed_engagement_source = 'debi'
        emailScores.computed_engagement_period = ''
        emailScores.computed_engagement_breakdown = null
      }
    } else if (engData) {
      const detail = buildMonthlyEngagementScoreDetail(engData)
      if (detail) {
        Object.assign(emailScores, detail)
      } else {
        emailScores.computed_engagement = null
      }
    } else {
      emailScores.computed_engagement = null
    }

    const perfData = perfByEmail[emp.email]
    if (perfData && perfData.total > 0) {
      const confidence = Math.min(Math.sqrt(perfData.total / 6), 1.0)
      const gemRate = perfData.gems / perfData.total
      const exceedingRate = perfData.exceedings / perfData.total
      emailScores.computed_performance = Math.round(gemRate * confidence * 1000) / 10
      emailScores.computed_exceeding_performance = Math.round(exceedingRate * confidence * 1000) / 10
    } else {
      emailScores.computed_performance = null
      emailScores.computed_exceeding_performance = null
    }

    if (includeWho5) {
      emailScores.computed_who5 = who5ByEmail[emp.email] !== undefined ? who5ByEmail[emp.email] : null
    }

    const fbSent = feedbackSentTo[emp.email] ? feedbackSentTo[emp.email].size : 0
    const fbReceived = feedbackReceivedFrom[emp.email] ? feedbackReceivedFrom[emp.email].size : 0
    let reciprocalPairs = 0
    const sentTo = recognitionSentTo[emp.email]
    const recvFrom = recognitionReceivedFrom[emp.email]
    if (sentTo && recvFrom) {
      sentTo.forEach(function (peer) { if (recvFrom.has(peer)) reciprocalPairs++ })
    }
    emailScores.computed_peer_trust = fbSent + fbReceived + reciprocalPairs

    const recData = recognitionByEmail[emp.email]
    allGivenCounts.push(recData ? (recData.given || 0) : 0)
    allReceivedCounts.push(recData ? (recData.received || 0) : 0)

    scores[emp.email] = emailScores
  }

  const medianGiven = getMedian(allGivenCounts)
  const medianReceived = getMedian(allReceivedCounts)

  for (let i = 0; i < employeeList.length; i++) {
    const emp = employeeList[i]
    if (!emp.email || !scores[emp.email]) continue
    const recData = recognitionByEmail[emp.email]
    const given = recData ? (recData.given || 0) : 0
    const received = recData ? (recData.received || 0) : 0

    scores[emp.email].computed_giving_recognition =
      Math.round(Math.min(given / (2 * medianGiven), 1) * 1000) / 10
    scores[emp.email].computed_receiving_recognition =
      Math.round(Math.min(received / (2 * medianReceived), 1) * 1000) / 10

    const feedbackGiven = feedbackGivenByEmail[emp.email] || 0
    const trulyCount = trulyReceivedByEmail[emp.email] || 0
    const feedbackGivenNorm = Math.min(feedbackGiven / 5, 1.0)
    const trulyNorm = Math.min(trulyCount / 5, 1.0)
    const peerHigh = Math.max(feedbackGivenNorm, trulyNorm)
    const peerLow = Math.min(feedbackGivenNorm, trulyNorm)
    const peerFeedbackActivity = peerHigh * 0.7 + peerLow * 0.3

    const givenNorm = Math.min(given / (2 * medianGiven), 1.0)
    const receivedNorm = Math.min(received / (2 * medianReceived), 1.0)
    const recHigh = Math.max(givenNorm, receivedNorm)
    const recLow = Math.min(givenNorm, receivedNorm)
    const recognitionReciprocity = recHigh * 0.7 + recLow * 0.3

    let networkStrength = 0.1
    const netType = (emp.network_type || '').toLowerCase()
    if (netType.indexOf('super') >= 0) networkStrength = 1.0
    else if (netType.indexOf('high') >= 0) networkStrength = 0.7
    else if (netType.indexOf('medium') >= 0) networkStrength = 0.4
    else if (netType.indexOf('low') >= 0) networkStrength = 0.1

    const alignInfo = alignmentByEmail[emp.email]
    const alignmentReceived = (alignInfo && alignInfo.totalReceived > 0)
      ? alignInfo.effortReceived / alignInfo.totalReceived : 0
    const recognitionQuality = Math.min(alignmentReceived / 5, 1.0)

    scores[emp.email].computed_trust = Math.round(
      (peerFeedbackActivity * 0.30 + recognitionReciprocity * 0.30 + networkStrength * 0.20 + recognitionQuality * 0.20) * 1000
    ) / 10
  }

  return scores
}

const peopleTableScorePromises: { [key: string]: Promise<any> } = {}
const peoplePrimaryMetricPromises: { [key: string]: Promise<any> } = {}
const peopleFullPrimaryMetricPromises: { [key: string]: Promise<any> } = {}
const peopleInsightApiPromises: { [key: string]: Promise<any> } = {}
const peopleDetailScorePromises: { [key: string]: Promise<any> } = {}
const personDailyCheckinPromises: { [key: string]: Promise<any> } = {}
const who5ScoreCacheByKey: { [key: string]: Record<string, number> } = {}
const who5ScoreLoadPromises: { [key: string]: Promise<{ cacheKey: string, scores: Record<string, number> }> } = {}

function fetchPeopleInsightApiSummary(company: string): Promise<any> {
  if (peopleInsightApiPromises[company]) {
    return peopleInsightApiPromises[company]
  }

  peopleInsightApiPromises[company] = apiNodeRequest({
    method: 'post',
    url: `/insight-people/${encodeURIComponent(company)}`,
    data: {},
    timeout: PEOPLE_INSIGHT_API_TIMEOUT_MS,
  })
    .then((response) => response.data)
    .finally(() => {
      delete peopleInsightApiPromises[company]
    })

  return peopleInsightApiPromises[company]
}

function getPeopleInsightApiScores(summary: any): Record<string, any> {
  const scores: Record<string, any> = {}
  const people = summary && Array.isArray(summary.people) ? summary.people : []

  people.forEach((person: any) => {
    if (!person || !person.email || !person.metrics) return
    const metrics = Object.assign({}, person.metrics)
    delete metrics.computed_engagement
    scores[person.email] = metrics
  })

  return scores
}

async function loadWho5ScoreBundle(company: string, now: Date): Promise<{ cacheKey: string, scores: Record<string, number> }> {
  const cacheKey = getQuarterCacheKey(company, now)
  if (who5ScoreCacheByKey[cacheKey]) {
    return { cacheKey, scores: who5ScoreCacheByKey[cacheKey] }
  }
  if (who5ScoreLoadPromises[cacheKey]) {
    return who5ScoreLoadPromises[cacheKey]
  }

  const currentDate = format(now, 'YYYY-MM-DD')
  const who5StartDate = getPreviousQuarterStartDate(now)

  who5ScoreLoadPromises[cacheKey] = (async () => {
    let snapshots: any[] = []
    try {
      const singleSnapshot = await firebase.firestore().collection('users_questions')
        .where('company', '==', company)
        .where('quest_id', 'in', WHO5_QUESTION_IDS)
        .where('date', '>=', who5StartDate)
        .where('date', '<=', currentDate)
        .get()
      snapshots = [singleSnapshot]
    } catch (error) {
      console.warn('Failed to load WHO-5 data in a single query, falling back to split queries:', error)
      snapshots = await Promise.all(WHO5_QUESTION_IDS.map((questionId) => {
        return firebase.firestore().collection('users_questions')
          .where('company', '==', company)
          .where('quest_id', '==', questionId)
          .where('date', '>=', who5StartDate)
          .where('date', '<=', currentDate)
          .get()
          .catch(e => {
            console.warn('Failed to load WHO-5 data for question:', questionId, e)
            return null
          })
      }))
    }

    const scores = buildWho5ScoresFromSnapshots((snapshots || []).filter((snapshot: any) => !!snapshot))
    who5ScoreCacheByKey[cacheKey] = scores
    return { cacheKey, scores }
  })().finally(() => {
    delete who5ScoreLoadPromises[cacheKey]
  })

  return who5ScoreLoadPromises[cacheKey]
}

const engagementRecordsCache: Record<string, any[]> = {}
const engagementRecordsLoadPromises: Record<string, Promise<any[]>> = {}
const individualDebiEngagementRecordsCache: Record<string, any[]> = {}
const individualDebiEngagementLoadPromises: Record<string, Promise<any[]>> = {}

function engagementRecordsCacheKey(company: string, now: Date): string {
  const currentMonth = format(now, 'YYYY-MM')
  const previousMonth = format(subMonths(now, 1), 'YYYY-MM')
  const monthKeys = currentMonth === previousMonth ? [currentMonth] : [currentMonth, previousMonth]
  return `${company}|${monthKeys.join(',')}`
}

async function fetchEngagementRecords(company: string, now: Date): Promise<any[]> {
  const cacheKey = engagementRecordsCacheKey(company, now)
  if (engagementRecordsCache[cacheKey]) {
    return engagementRecordsCache[cacheKey]
  }
  if (engagementRecordsLoadPromises[cacheKey]) {
    return engagementRecordsLoadPromises[cacheKey]
  }

  const currentMonth = format(now, 'YYYY-MM')
  const previousMonth = format(subMonths(now, 1), 'YYYY-MM')
  const monthKeys = currentMonth === previousMonth ? [currentMonth] : [currentMonth, previousMonth]

  engagementRecordsLoadPromises[cacheKey] = firebase.firestore().collection('report_monthly')
    .where('company', '==', company)
    .where('monthyear', 'in', monthKeys)
    .get()
    .then((snapshot) => {
      const records = snapshotToRecords(snapshot)
      engagementRecordsCache[cacheKey] = records
      return records
    })
    .finally(() => {
      delete engagementRecordsLoadPromises[cacheKey]
    })

  return engagementRecordsLoadPromises[cacheKey]
}

async function fetchIndividualDebiEngagementRecords(company: string, now: Date): Promise<any[]> {
  const endDate = format(subDays(now, 1), 'YYYY-MM-DD')
  const startDate = format(subDays(now, PEOPLE_ENGAGEMENT_WINDOW_DAYS), 'YYYY-MM-DD')
  const cacheKey = `${company}|${startDate}|${endDate}`

  if (individualDebiEngagementRecordsCache[cacheKey]) {
    return individualDebiEngagementRecordsCache[cacheKey]
  }
  if (individualDebiEngagementLoadPromises[cacheKey]) {
    return individualDebiEngagementLoadPromises[cacheKey]
  }

  individualDebiEngagementLoadPromises[cacheKey] = pgEngagementIndexDay(company, startDate, endDate)
    .then((recordsByKey) => {
      if (!recordsByKey) {
        throw new Error('Individual DEBI engagement records unavailable')
      }

      const records = Object.keys(recordsByKey).map((key) => Object.assign({}, recordsByKey[key], {
        _engagementPeriod: `${startDate} to ${endDate}`,
      }))
      if (records.length === 0) {
        throw new Error('Individual DEBI engagement records empty')
      }
      individualDebiEngagementRecordsCache[cacheKey] = records
      return records
    })
    .finally(() => {
      delete individualDebiEngagementLoadPromises[cacheKey]
    })

  return individualDebiEngagementLoadPromises[cacheKey]
}

type PeopleEngagementInputResult = {
  monthlyRecords: any[];
  debiRecords: any[] | null;
  loaded: boolean;
  source: 'debi' | 'monthly' | 'none';
}

async function loadPeopleEngagementInputs(company: string, now: Date): Promise<PeopleEngagementInputResult> {
  try {
    const debiRecords = await fetchIndividualDebiEngagementRecords(company, now)
    return {
      monthlyRecords: [],
      debiRecords,
      loaded: true,
      source: 'debi',
    }
  } catch (error) {
    console.warn('Failed to load individual DEBI engagement data; falling back to monthly engagement fields:', error)
  }

  try {
    const monthlyRecords = await fetchEngagementRecords(company, now)
    return {
      monthlyRecords,
      debiRecords: null,
      loaded: true,
      source: 'monthly',
    }
  } catch (error) {
    console.warn('Failed to load People primary engagement data:', error)
    return {
      monthlyRecords: [],
      debiRecords: null,
      loaded: false,
      source: 'none',
    }
  }
}

export type RelationshipRecordsBundle = {
  reviewRecords: any[],
  hereRecords: any[],
  feedbackRecords: any[],
}

const relationshipRecordsCache: Record<string, RelationshipRecordsBundle> = {}
const relationshipRecordsLoadPromises: Record<string, Promise<RelationshipRecordsBundle>> = {}

function relationshipRecordsCacheKey(company: string, past180: string): string {
  return `${company}|${past180}`
}

export function peekSharedRelationshipRecords(company: string, past180: string): RelationshipRecordsBundle | null {
  return relationshipRecordsCache[relationshipRecordsCacheKey(company, past180)] || null
}

async function fetchFullRelationshipRecords(company: string, past180: string): Promise<RelationshipRecordsBundle> {
  const [
    reviewSnap,
    hereSnap,
    feedbackSnap,
  ] = await Promise.all([
    firebase.firestore().collection('send_review_gem')
      .where('company', '==', company)
      .where('createdate', '>=', past180)
      .get(),
    firebase.firestore().collection('here_point')
      .where('company', '==', company)
      .where('date', '>=', past180)
      .where('statusrow', 'in', ['sendcoin', 'sendgem'])
      .get(),
    firebase.firestore().collection('peerfeedback')
      .where('company', '==', company)
      .where('date', '>=', past180)
      .get(),
  ])

  return {
    reviewRecords: snapshotToRecords(reviewSnap),
    hereRecords: snapshotToRecords(hereSnap),
    feedbackRecords: snapshotToRecords(feedbackSnap),
  }
}

function filterRelationshipRecordsForEmployees(
  records: RelationshipRecordsBundle,
  employees: any[],
  options: { hereStatusrows?: string[] } = {}
): RelationshipRecordsBundle {
  const emailSet = new Set(getEmployeeEmails(employees).slice(0, PEOPLE_PRIMARY_VISIBLE_LIMIT))
  if (emailSet.size === 0) {
    return {
      reviewRecords: [],
      hereRecords: [],
      feedbackRecords: [],
    }
  }

  const hereStatusrows = options.hereStatusrows

  return {
    reviewRecords: (records.reviewRecords || []).filter((record: any) => (
      record && record.receiver && emailSet.has(record.receiver)
    )),
    hereRecords: (records.hereRecords || []).filter((record: any) => {
      if (!record) return false
      if (
        hereStatusrows &&
        hereStatusrows.length > 0 &&
        record.statusrow &&
        hereStatusrows.indexOf(record.statusrow) === -1
      ) {
        return false
      }
      return (
        (record.sender_email && emailSet.has(record.sender_email)) ||
        (record.send_to_email && emailSet.has(record.send_to_email))
      )
    }),
    feedbackRecords: (records.feedbackRecords || []).filter((record: any) => (
      record && (
        (record.email && emailSet.has(record.email)) ||
        (record.email_to && emailSet.has(record.email_to))
      )
    )),
  }
}

async function loadVisibleRelationshipRecords(
  company: string,
  past180: string,
  employees: any[]
): Promise<RelationshipRecordsBundle | null> {
  try {
    const sharedRecords = await getSharedRelationshipRecords(company, past180)
    return filterRelationshipRecordsForEmployees(sharedRecords, employees, { hereStatusrows: ['sendcoin'] })
  } catch (error) {
    console.warn('Failed to load visible People metrics from shared relationship cache; falling back to chunked queries:', error)
    return fetchVisibleRelationshipRecords(company, past180, employees)
  }
}

export async function getSharedRelationshipRecords(company: string, past180: string): Promise<RelationshipRecordsBundle> {
  const cacheKey = relationshipRecordsCacheKey(company, past180)
  if (relationshipRecordsCache[cacheKey]) {
    return relationshipRecordsCache[cacheKey]
  }
  if (relationshipRecordsLoadPromises[cacheKey]) {
    return relationshipRecordsLoadPromises[cacheKey]
  }

  relationshipRecordsLoadPromises[cacheKey] = fetchFullRelationshipRecords(company, past180)
    .then((records) => {
      relationshipRecordsCache[cacheKey] = records
      return records
    })
    .finally(() => {
      delete relationshipRecordsLoadPromises[cacheKey]
    })

  return relationshipRecordsLoadPromises[cacheKey]
}

async function fetchVisibleRelationshipRecords(company: string, past180: string, employees: any[]): Promise<{
  reviewRecords: any[],
  hereRecords: any[],
  feedbackRecords: any[],
} | null> {
  const emails = getEmployeeEmails(employees).slice(0, PEOPLE_PRIMARY_VISIBLE_LIMIT)
  if (emails.length === 0) {
    return {
      reviewRecords: [],
      hereRecords: [],
      feedbackRecords: [],
    }
  }

  try {
    const chunks = chunkValues(emails, EMAIL_QUERY_CHUNK_SIZE)
    const reviewPromises = chunks.map((chunk) => firebase.firestore().collection('send_review_gem')
      .where('company', '==', company)
      .where('receiver', 'in', chunk)
      .where('createdate', '>=', past180)
      .get())
    const hereSentPromises = chunks.map((chunk) => firebase.firestore().collection('here_point')
      .where('company', '==', company)
      .where('sender_email', 'in', chunk)
      .where('date', '>=', past180)
      .where('statusrow', '==', 'sendcoin')
      .get())
    const hereReceivedPromises = chunks.map((chunk) => firebase.firestore().collection('here_point')
      .where('company', '==', company)
      .where('send_to_email', 'in', chunk)
      .where('date', '>=', past180)
      .where('statusrow', '==', 'sendcoin')
      .get())
    const feedbackSentPromises = chunks.map((chunk) => firebase.firestore().collection('peerfeedback')
      .where('company', '==', company)
      .where('email', 'in', chunk)
      .where('date', '>=', past180)
      .get())
    const feedbackReceivedPromises = chunks.map((chunk) => firebase.firestore().collection('peerfeedback')
      .where('company', '==', company)
      .where('email_to', 'in', chunk)
      .where('date', '>=', past180)
      .get())

    const [
      reviewSnaps,
      hereSentSnaps,
      hereReceivedSnaps,
      feedbackSentSnaps,
      feedbackReceivedSnaps,
    ] = await Promise.all([
      Promise.all(reviewPromises),
      Promise.all(hereSentPromises),
      Promise.all(hereReceivedPromises),
      Promise.all(feedbackSentPromises),
      Promise.all(feedbackReceivedPromises),
    ])

    return {
      reviewRecords: dedupeRecords(reviewSnaps.reduce((records: any[], snap: any) => records.concat(snapshotToRecords(snap)), [])),
      hereRecords: dedupeRecords(
        hereSentSnaps
          .concat(hereReceivedSnaps)
          .reduce((records: any[], snap: any) => records.concat(snapshotToRecords(snap)), [])
      ),
      feedbackRecords: dedupeRecords(
        feedbackSentSnaps
          .concat(feedbackReceivedSnaps)
          .reduce((records: any[], snap: any) => records.concat(snapshotToRecords(snap)), [])
      ),
    }
  } catch (error) {
    console.warn('Failed to load visible-row People metrics with chunked queries; falling back to full company metrics:', error)
    return null
  }
}

export const AXIS_OPTIONS = [
  { key: 'impact_rating', label: 'Impact Rating', min: 0, max: 150, dynamic: true, highIsGood: true,
    description: 'Overall contribution score based on engagement behavior and organizational impact.',
    dataNote: 'Composite score derived from check-in activity, feedback participation, recognition patterns, and team involvement. Updated weekly. Scores above 70 indicate strong engagement.' },
  { key: 'computed_flight_risk', label: 'Flight Risk Score', min: 0, max: 100, dynamic: false, highIsGood: false,
    description: 'Likelihood of disengagement based mainly on unhappy check-in patterns.',
    dataNote: 'Uses red (unhappy) daily check-ins over the last 90 days as the strongest signal. Recent reds count more, 3+ red days can show a chronic pattern, and 3+ expected check-in days in a row can show an acute pattern. Low well-being or weak connections can lift weak signals into Watch, but cannot create high risk on their own. People with no usable daily check-in data show no score instead of Safe.' },
  { key: 'computed_engagement', label: 'Engagement Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How actively someone participates across check-ins, feedback, and recognition.',
    dataNote: 'Combines 6 monthly indicators: daily check-ins (30%), open-ended feedback (20%), recognition activity (20%), manager feedback (10%), recognition quality (10%), and peer feedback (10%). Based on the most recent month.' },
  { key: 'computed_trust', label: 'Trust Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How trusted and valued someone is by their peers.',
    dataNote: 'Combines peer feedback activity (30%), recognition reciprocity (30%), network relationship depth (20%), and recognition quality received (20%). Based on last 6 months.' },
  { key: 'computed_focus', label: 'Focus Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How actively someone sets focus items for their development.',
    dataNote: 'Based on the number of active focus items. Each item contributes 20 points, up to 5 items for a maximum score of 100.' },
  { key: 'computed_goal_alignment', label: 'Goal Alignment', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How well someone\'s focus items connect to team goals.',
    dataNote: 'Percentage of focus items linked to a team goal. Shows 0 if no focus items are set.' },
  { key: 'owned_key_results', label: 'KRs Owned', min: 0, max: 10, dynamic: true, highIsGood: true,
    description: 'How many key results are assigned to this person across team goals.',
    dataNote: 'Counts key results where the person is listed as an owner in the goals data. Updates when goals/key results change.' },
  { key: 'kr_progress', label: 'KR Progress', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'Average progress across the key results this person owns.',
    dataNote: 'For individually tracked key results, uses that person\'s own progress. For collaborative key results, uses the shared KR progress. Shown as an average percentage.' },
  { key: 'kr_health', label: 'KR Health', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'Share of owned key results that are currently healthy.',
    dataNote: 'Counts owned key results as healthy when progress is on track or completed. Uses the same progress thresholds as goals: 60%+ is healthy, 100% is completed.' },
  { key: 'computed_performance', label: 'Performance Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How consistently someone earns gems in performance boosts.',
    dataNote: 'Gem rate (culture and goals both rated 3+) weighted by number of interactions. More boosts with gems = higher score. Based on last 6 months.' },
  { key: 'computed_giving_recognition', label: 'Giving Recognition', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How actively someone recognizes others\' contributions.',
    dataNote: 'Number of recognitions given over the past 6 months, normalized against the team median. A score of 50 means typical activity; 100 means twice the median or more.' },
  { key: 'computed_receiving_recognition', label: 'Receiving Recognition', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How much recognition someone receives from peers.',
    dataNote: 'Number of recognitions received over the past 6 months, normalized against the team median. A score of 50 means typical recognition; 100 means twice the median or more.' },
  { key: 'healthbar', label: 'Happiness Score', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'Current happiness and well-being level.',
    dataNote: 'Based on daily check-in responses and survey data. Reflects recent mood trends.' },
  { key: 'computed_who5', label: 'WHO-5 Well-Being', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'Quarterly well-being measurement based on the latest completed WHO-5 score for the individual.',
    dataNote: 'Based on quarterly WHO-5 well-being measurements. Uses the latest date where all 5 WHO-5 questions were completed and scores it on the standard 0-100 WHO-5 scale.' },
  { key: 'computed_checkin_consistency', label: 'Check-in Consistency', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How consistently someone submits daily check-ins over the last 90 days.',
    dataNote: 'Percentage of issued daily happiness check-ins completed over the trailing 90 days. Days when no check-in was issued, such as holidays or scheduled days off, are not treated as missed check-ins.' },
  { key: 'computed_manager_responsiveness', label: 'Manager Responsiveness', min: 0, max: 100, dynamic: false, highIsGood: true,
    description: 'How responsive someone\'s direct manager is to their team.',
    dataNote: 'Uses the direct manager\'s feedback reply rate from the managers insights data over the last 30 days. This is mapped from employee to manager using the employee\'s current boss relationship.' },
  { key: 'computed_peer_trust', label: 'Peer Trust Count', min: 0, max: 15, dynamic: true, highIsGood: true,
    description: 'Breadth of peer relationships through feedback and mutual recognition.',
    dataNote: 'Count of unique peers given feedback to, unique peers received feedback from, and reciprocal recognition pairs (mutual give-and-receive). Based on last 6 months.' },
  { key: 'medalCount', label: 'Recognition Medals', min: 0, max: 30, dynamic: true, highIsGood: true,
    description: 'Total special recognition medals earned.',
    dataNote: 'Cumulative count of medals received across all recognition events. Not time-bounded.' },
  { key: 'ps_empathy', label: 'Empathy (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Ability to understand and share the feelings of others.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_self_awareness', label: 'Self-Awareness (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Understanding of own emotions, strengths, and limitations.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_critical_thinking', label: 'Critical Thinking (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Ability to analyze situations and make reasoned decisions.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_leadership', label: 'Leadership (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Ability to guide, inspire, and influence others.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_initiative_making', label: 'Initiative (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Proactiveness in taking action and driving outcomes.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
  { key: 'ps_optimism', label: 'Optimism (EQ)', min: 0, max: 10, dynamic: false, highIsGood: true,
    description: 'Positive outlook and resilience in the face of challenges.',
    dataNote: 'Score from 0-10 based on power skills assessment responses over time.' },
]

export default {
  namespaced: true,
  state: {
    selectedXAxis: 'impact_rating',
    selectedYAxis: 'healthbar',
    selectedTeam: '',
    colorBy: 'talent_type',
    loading: false,
    loadingCount: 0,
    selectedEmployee: null,
    compositeScores: {} as Record<string, any>,
    compositeLoaded: false,
    compositeCompany: '',
    tableScoresLoaded: false,
    tableScoresCompany: '',
    detailScoresLoaded: false,
    detailScoresCompany: '',
    who5Loaded: false,
    who5ScoresCompany: '',
    who5ScoresCacheKey: '',
    dailyCheckinsLoaded: false,
    primaryMetricsLoading: false,
    primaryMetricsCompany: '',
    visiblePrimaryMetricsCompany: '',
  },
  mutations: {
    setSelectedXAxis(state: any, value: string) {
      state.selectedXAxis = value
    },
    setSelectedYAxis(state: any, value: string) {
      state.selectedYAxis = value
    },
    setSelectedTeam(state: any, value: string) {
      state.selectedTeam = value
    },
    setColorBy(state: any, value: string) {
      state.colorBy = value
    },
    setLoading(state: any, value: boolean) {
      if (value) {
        state.loadingCount = (state.loadingCount || 0) + 1
      } else {
        state.loadingCount = Math.max((state.loadingCount || 0) - 1, 0)
      }
      state.loading = state.loadingCount > 0
    },
    setSelectedEmployee(state: any, value: any) {
      state.selectedEmployee = value
    },
    setPrimaryMetricsLoading(state: any, payload: any) {
      state.primaryMetricsLoading = !!(payload && payload.loading)
      state.primaryMetricsCompany = payload && payload.company ? payload.company : state.primaryMetricsCompany
    },
    setCompositeScores(state: any, payload: any) {
      const incomingScores = payload && payload.scores ? payload.scores : {}
      state.compositeScores = payload && payload.merge
        ? mergeScoresByEmail(state.compositeScores, incomingScores)
        : incomingScores

      const company = payload && payload.company ? payload.company : ''
      const scope = payload && payload.scope ? payload.scope : 'full'
      const hasWho5Payload = !!(payload && payload.who5Loaded)

      if (company && state.who5ScoresCompany && state.who5ScoresCompany !== company) {
        state.who5Loaded = false
        state.who5ScoresCompany = ''
        state.who5ScoresCacheKey = ''
      }

      if (scope === 'table') {
        state.tableScoresLoaded = true
        state.tableScoresCompany = company
        state.primaryMetricsCompany = company
        state.primaryMetricsLoading = false
      } else if (scope === 'primary-visible') {
        state.visiblePrimaryMetricsCompany = company
      } else if (scope === 'primary-cache') {
        state.primaryMetricsCompany = company || state.primaryMetricsCompany
      } else if (scope === 'detail') {
        state.detailScoresLoaded = true
        state.detailScoresCompany = company
      } else if (scope === 'wellbeing') {
        // WHO-5 is loaded as a lightweight cached bundle.
      } else if (scope === 'person-detail') {
        // Per-person detail scores are intentionally lazy-loaded and should not
        // mark company-wide score bundles as complete.
      } else {
        const sameCompany = state.compositeCompany === company
        state.compositeLoaded = true
        state.compositeCompany = company
        state.tableScoresLoaded = true
        state.tableScoresCompany = company
        state.detailScoresLoaded = true
        state.detailScoresCompany = company
        state.who5Loaded = hasWho5Payload || (sameCompany && state.who5Loaded)
        state.who5ScoresCompany = state.who5Loaded ? company : ''
        state.who5ScoresCacheKey = state.who5Loaded
          ? ((payload && payload.who5CacheKey) || state.who5ScoresCacheKey || '')
          : ''
        state.dailyCheckinsLoaded = !!(payload && payload.dailyCheckinsLoaded) || (sameCompany && state.dailyCheckinsLoaded)
      }

      if (hasWho5Payload && company) {
        state.who5Loaded = true
        state.who5ScoresCompany = company
        state.who5ScoresCacheKey = (payload && payload.who5CacheKey) || state.who5ScoresCacheKey || ''
      }
    },
  },
  getters: {
    selectedXAxis: (state: any) => state.selectedXAxis,
    selectedYAxis: (state: any) => state.selectedYAxis,
    selectedTeam: (state: any) => state.selectedTeam,
    colorBy: (state: any) => state.colorBy,
    loading: (state: any) => state.loading,
    selectedEmployee: (state: any) => state.selectedEmployee,
    compositeScores: (state: any) => state.compositeScores,
    tableScoresLoaded: (state: any) => state.tableScoresLoaded,
    detailScoresLoaded: (state: any) => state.detailScoresLoaded,
  },
  actions: {
    async loadMatrixData({ dispatch, rootState, commit }: any, user: any) {
      commit('setLoading', true)
      try {
        const peopleState = rootState.people || {}
        const hasMembers = listMatchesCompany(peopleState.membersall, user.company)
        const hasStarPerform = listMatchesCompany(peopleState.starperformall, user.company)

        if (!hasMembers || !hasStarPerform) {
          await dispatch('people/getStarAndRiskPerform', user, { root: true })
        }
      } finally {
        commit('setLoading', false)
      }
    },

    async loadPeoplePrimaryMetrics({ state, rootState, dispatch, commit }: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      if (!user || !user.company) return

      const now = payload && payload.now
        ? new Date(payload.now)
        : toCompanyDate(new Date(), await getCompanyTimezone(user.company))
      const currentDate = format(now, 'YYYY-MM-DD')
      const past180 = format(subDays(now, 180), 'YYYY-MM-DD')
      const who5CacheKey = getQuarterCacheKey(user.company, now)
      const promiseKey = `${user.company}|${currentDate}`

      if (peoplePrimaryMetricPromises[promiseKey]) {
        return peoplePrimaryMetricPromises[promiseKey]
      }

      let employees = payload && Array.isArray(payload.employees) ? payload.employees.filter((item: any) => !!item) : []
      if (employees.length === 0) {
        const peopleState = rootState.people || {}
        const hasStarPerform = listMatchesCompany(peopleState.starperformall, user.company)
        if (!hasStarPerform) {
          await dispatch('people/getStarAndRiskPerform', user, { root: true })
        }
        const nextPeopleState = rootState.people || {}
        employees = listMatchesCompany(nextPeopleState.starperformall, user.company)
          ? nextPeopleState.starperformall.filter((item: any) => !!item)
          : []
      }

      employees = uniqueEmployeesByEmail(employees)
      if (employees.length === 0) return

      const visibleEmployees = uniqueEmployeesByEmail(
        payload && Array.isArray(payload.visibleEmployees) && payload.visibleEmployees.length > 0
          ? payload.visibleEmployees
          : ((rootState.people && rootState.people.starperform) || employees.slice(0, PEOPLE_PRIMARY_VISIBLE_LIMIT))
      )

      if (
        state.tableScoresLoaded &&
        state.tableScoresCompany === user.company &&
        state.who5Loaded &&
        state.who5ScoresCompany === user.company &&
        state.who5ScoresCacheKey === who5CacheKey &&
        hasScoreFieldsForEmployees(state.compositeScores, employees, PEOPLE_TABLE_SCORE_KEYS.concat(PEOPLE_WELLBEING_SCORE_KEYS))
      ) {
        return
      }

      const cachedScores = readPeoplePrimaryMetricCache(user.company, now)
      if (Object.keys(cachedScores).length > 0) {
        commit('setCompositeScores', {
          scores: cachedScores,
          company: user.company,
          scope: 'primary-cache',
          merge: true,
          who5Loaded: Object.keys(filterScoresForCache(cachedScores, PEOPLE_PRIMARY_WHO5_FIELDS)).length > 0,
          who5CacheKey,
        })
      }

      commit('setLoading', true)
      commit('setPrimaryMetricsLoading', { company: user.company, loading: true })
      peoplePrimaryMetricPromises[promiseKey] = (async () => {
        const engagementPromise = loadPeopleEngagementInputs(user.company, now)

        try {
          try {
            const summary = await fetchPeopleInsightApiSummary(user.company)
            let apiScores = getPeopleInsightApiScores(summary)

            if (Object.keys(apiScores).length === 0) {
              throw new Error('People Insight API returned no metric scores')
            }

            const apiDetailsLoaded = await dispatch(
              'people/applyPeopleInsightApiDetails',
              { user, details: summary && summary.details },
              { root: true }
            )
            commit('setCompositeScores', {
              scores: apiScores,
              company: user.company,
              scope: 'table',
              merge: true,
              who5Loaded: true,
              who5CacheKey,
            })

            const engagementResult = await engagementPromise
            if (engagementResult.loaded) {
              const engagementScores = filterScoresForCache(
                buildPeoplePrimaryMetricScores(
                  employees,
                  engagementResult.monthlyRecords,
                  [],
                  [],
                  [],
                  {},
                  false,
                  engagementResult.debiRecords
                ),
                PEOPLE_PRIMARY_ENGAGEMENT_FIELDS
              )
              apiScores = mergeScoresByEmail(apiScores, engagementScores)
              commit('setCompositeScores', {
                scores: engagementScores,
                company: user.company,
                scope: 'primary-visible',
                merge: true,
              })
            }
            writePeoplePrimaryMetricCaches(user.company, now, apiScores)
            return summary
          } catch (error) {
            console.warn(
              '[people insight] API failed, using existing Firestore metric loaders',
              error
            )
          }

          const [
            engagementResult,
            visibleRelationships,
          ] = await Promise.all([
            engagementPromise,
            loadVisibleRelationshipRecords(user.company, past180, visibleEmployees),
          ])

          const engagementLoaded = engagementResult.loaded
          if (visibleRelationships) {
            let visibleScores = buildPeoplePrimaryMetricScores(
              visibleEmployees,
              engagementResult.monthlyRecords,
              visibleRelationships.reviewRecords,
              visibleRelationships.hereRecords,
              visibleRelationships.feedbackRecords,
              {},
              false,
              engagementResult.debiRecords
            )
            if (!engagementLoaded) {
              visibleScores = omitScoreFields(visibleScores, PEOPLE_PRIMARY_ENGAGEMENT_FIELDS)
            }
            if (Object.keys(visibleScores).length > 0) {
              commit('setCompositeScores', {
                scores: visibleScores,
                company: user.company,
                scope: 'primary-visible',
                merge: true,
              })
            }
          }
        } finally {
          commit('setLoading', false)
          commit('setPrimaryMetricsLoading', { company: user.company, loading: false })
          delete peoplePrimaryMetricPromises[promiseKey]
        }
      })()

      return peoplePrimaryMetricPromises[promiseKey]
    },

    async loadPeopleFullPrimaryMetrics({ state, rootState, dispatch, commit }: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      if (!user || !user.company) return

      const now = payload && payload.now
        ? new Date(payload.now)
        : toCompanyDate(new Date(), await getCompanyTimezone(user.company))
      const currentDate = format(now, 'YYYY-MM-DD')
      const past180 = format(subDays(now, 180), 'YYYY-MM-DD')
      const who5CacheKey = getQuarterCacheKey(user.company, now)
      const promiseKey = `${user.company}|${currentDate}`

      let employees = payload && Array.isArray(payload.employees)
        ? payload.employees.filter((item: any) => !!item)
        : []
      if (employees.length === 0) {
        const peopleState = rootState.people || {}
        if (!listMatchesCompany(peopleState.starperformall, user.company)) {
          await dispatch('people/getStarAndRiskPerform', user, { root: true })
        }
        const nextPeopleState = rootState.people || {}
        employees = listMatchesCompany(nextPeopleState.starperformall, user.company)
          ? nextPeopleState.starperformall.filter((item: any) => !!item)
          : []
      }

      employees = uniqueEmployeesByEmail(employees)
      if (employees.length === 0) return

      if (
        state.tableScoresLoaded &&
        state.tableScoresCompany === user.company &&
        state.who5Loaded &&
        state.who5ScoresCompany === user.company &&
        state.who5ScoresCacheKey === who5CacheKey &&
        hasScoreFieldsForEmployees(
          state.compositeScores,
          employees,
          PEOPLE_TABLE_SCORE_KEYS.concat(PEOPLE_WELLBEING_SCORE_KEYS)
        )
      ) {
        return
      }

      if (peopleFullPrimaryMetricPromises[promiseKey]) {
        return peopleFullPrimaryMetricPromises[promiseKey]
      }

      peopleFullPrimaryMetricPromises[promiseKey] = (async () => {
        try {
          const engagementPromise = loadPeopleEngagementInputs(user.company, now)
          const relationshipPromise = getSharedRelationshipRecords(user.company, past180)
            .then((records) => ({ records, loaded: true }))
            .catch((error) => {
              console.warn('Failed to load full People primary relationship data:', error)
              return { records: null, loaded: false }
            })
          const who5Promise = loadWho5ScoreBundle(user.company, now)
            .catch((error) => {
              console.warn('Failed to load People primary WHO-5 data:', error)
              return null
            })

          const [engagementResult, relationshipResult, who5Bundle] = await Promise.all([
            engagementPromise,
            relationshipPromise,
            who5Promise,
          ])
          const relationshipRecords = relationshipResult.records

          let fullScores = buildPeoplePrimaryMetricScores(
            employees,
            engagementResult.monthlyRecords,
            relationshipRecords ? relationshipRecords.reviewRecords : [],
            relationshipRecords ? relationshipRecords.hereRecords : [],
            relationshipRecords ? relationshipRecords.feedbackRecords : [],
            who5Bundle ? who5Bundle.scores : {},
            !!who5Bundle,
            engagementResult.debiRecords
          )
          if (!engagementResult.loaded) {
            fullScores = omitScoreFields(fullScores, PEOPLE_PRIMARY_ENGAGEMENT_FIELDS)
          }
          if (!relationshipResult.loaded) {
            fullScores = omitScoreFields(fullScores, PEOPLE_PRIMARY_RELATIONSHIP_FIELDS)
          }

          commit('setCompositeScores', {
            scores: fullScores,
            company: user.company,
            scope: 'table',
            merge: true,
            who5Loaded: !!who5Bundle,
            who5CacheKey: who5Bundle ? who5Bundle.cacheKey : who5CacheKey,
          })
          writePeoplePrimaryMetricCaches(user.company, now, fullScores)
        } finally {
          delete peopleFullPrimaryMetricPromises[promiseKey]
        }
      })()

      return peopleFullPrimaryMetricPromises[promiseKey]
    },

    async loadPeopleTableScores(context: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      if (!user || !user.company) return

      if (peopleTableScorePromises[user.company]) {
        return peopleTableScorePromises[user.company]
      }

      peopleTableScorePromises[user.company] = context.dispatch('loadPeoplePrimaryMetrics', payload)
        .then(() => context.dispatch('loadPeopleFullPrimaryMetrics', payload))
        .finally(() => {
          delete peopleTableScorePromises[user.company]
        })

      return peopleTableScorePromises[user.company]
    },

    async loadPeopleWellbeingScores({ state, rootState, dispatch, commit }: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      if (!user || !user.company) return

      const timezone = await getCompanyTimezone(user.company)
      const now = toCompanyDate(new Date(), timezone)
      const cacheKey = getQuarterCacheKey(user.company, now)
      let employees = payload && Array.isArray(payload.employees) ? payload.employees.filter((item: any) => !!item) : []
      if (
        state.who5Loaded &&
        state.who5ScoresCompany === user.company &&
        state.who5ScoresCacheKey === cacheKey &&
        hasScoreFieldsForEmployees(state.compositeScores, employees, PEOPLE_WELLBEING_SCORE_KEYS)
      ) {
        return
      }

      if (employees.length === 0) {
        const peopleState = rootState.people || {}
        const hasStarPerform = listMatchesCompany(peopleState.starperformall, user.company)
        if (!hasStarPerform) {
          await dispatch('people/getStarAndRiskPerform', user, { root: true })
        }
        const nextPeopleState = rootState.people || {}
        employees = listMatchesCompany(nextPeopleState.starperformall, user.company)
          ? nextPeopleState.starperformall.filter((item: any) => !!item)
          : []
      }

      if (
        state.who5Loaded &&
        state.who5ScoresCompany === user.company &&
        state.who5ScoresCacheKey === cacheKey &&
        hasScoreFieldsForEmployees(state.compositeScores, employees, PEOPLE_WELLBEING_SCORE_KEYS)
      ) {
        return
      }

      if (employees.length === 0) {
        return
      }

      const who5Bundle = await loadWho5ScoreBundle(user.company, now)
      const scores: Record<string, any> = {}
      employees.forEach((emp: any) => {
        if (!emp || !emp.email) return
        scores[emp.email] = {
          computed_who5: who5Bundle.scores[emp.email] !== undefined ? who5Bundle.scores[emp.email] : null,
        }
      })

      commit('setCompositeScores', {
        scores,
        company: user.company,
        scope: 'wellbeing',
        merge: true,
        who5Loaded: true,
        who5CacheKey: who5Bundle.cacheKey,
      })
    },

    async loadPeopleDetailScores({ state, rootState, dispatch, commit }: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      if (!user || !user.company) return

      if (state.detailScoresLoaded && state.detailScoresCompany === user.company) {
        return
      }
      if (peopleDetailScorePromises[user.company]) {
        return peopleDetailScorePromises[user.company]
      }

      commit('setLoading', true)
      peopleDetailScorePromises[user.company] = (async () => {
        try {
          const scores: Record<string, any> = {}
          const timezone = await getCompanyTimezone(user.company)
          const now = toCompanyDate(new Date(), timezone)
          const currentDate = format(now, 'YYYY-MM-DD')
          const past90 = format(subDays(now, 90), 'YYYY-MM-DD')

          let peopleState = rootState.people || {}
          if (!listMatchesCompany(peopleState.starperformall, user.company)) {
            await dispatch('people/getStarAndRiskPerform', user, { root: true })
          }

          peopleState = rootState.people || {}
          const employees = listMatchesCompany(peopleState.starperformall, user.company)
            ? peopleState.starperformall.filter((item: any) => !!item)
            : (listMatchesCompany(peopleState.membersall, user.company)
              ? peopleState.membersall.filter((item: any) => !!item)
              : [])

          if (employees.length === 0) {
            return
          }

          let allUsersByKey = (rootState.main && rootState.main.allUser) ? rootState.main.allUser : {}
          if (!allUsersByKey || Object.keys(allUsersByKey).length === 0) {
            await dispatch('main/getuser', false, { root: true })
            allUsersByKey = (rootState.main && rootState.main.allUser) ? rootState.main.allUser : {}
          }

          const monthKeys: string[] = []
          for (let i = 0; i < 10; i++) {
            monthKeys.push(format(subMonths(now, i), 'YYYY-MM'))
          }
          const uniqueMonths = monthKeys.filter((value, index, arr) => arr.indexOf(value) === index)

          const [
            monthlySnap,
            managerInsightSnap,
            holidaySnap,
          ] = await Promise.all([
            firebase.firestore().collection('report_monthly')
              .where('company', '==', user.company)
              .where('monthyear', 'in', uniqueMonths)
              .get().catch(e => { console.warn('Failed to load report_monthly detail data:', e); return null }),
            firebase.firestore().collection('insight_manager_today')
              .where('company', '==', user.company)
              .where('date', '>=', format(subDays(now, 30), 'YYYY-MM-DD'))
              .where('date', '<=', currentDate)
              .orderBy('date')
              .get().catch(e => { console.warn('Failed to load manager responsiveness data:', e); return null }),
            firebase.firestore().collection('holliday')
              .where('companyid', '==', user.company)
              .get().catch(e => { console.warn('Failed to load holidays:', e); return null }),
          ])

          const dailyAnswerColorByEmail: Record<string, Record<string, string>> = {}
          const managerFeedbackTotalsByEmail: Record<string, { read: number, receive: number }> = {}
          const setDailyAnswer = (email: string, dateKey: string, color: string) => {
            setDailyAnswerColor(dailyAnswerColorByEmail, email, dateKey, color, past90, currentDate)
          }

          if (monthlySnap) {
            monthlySnap.forEach((doc: any) => {
              const data = doc.data()
              if (!data.email || !data.daily_answer) return
              const answers = data.daily_answer
              for (const date in answers) {
                setDailyAnswer(data.email, date, answers[date])
              }
            })
          }

          if (managerInsightSnap) {
            managerInsightSnap.forEach((doc: any) => {
              const data = doc.data()
              if (!data.email || data.type !== 'feedback') return

              const receive = parseAnswerValue(data.receive)
              const rawRead = parseAnswerValue(data.read)
              if (receive === null || rawRead === null) return

              const read = rawRead > receive ? receive : rawRead
              if (!managerFeedbackTotalsByEmail[data.email]) {
                managerFeedbackTotalsByEmail[data.email] = { read: 0, receive: 0 }
              }

              managerFeedbackTotalsByEmail[data.email].read += read
              managerFeedbackTotalsByEmail[data.email].receive += receive
            })
          }

          const managerResponsivenessByEmail: Record<string, number> = {}
          Object.keys(managerFeedbackTotalsByEmail).forEach((email) => {
            const totals = managerFeedbackTotalsByEmail[email]
            managerResponsivenessByEmail[email] = totals.receive > 0
              ? clampPercent((totals.read * 100) / totals.receive)
              : 0
          })

          const getManagerEmailForEmployee = (emp: any): string => {
            if (emp && typeof emp.boss === 'string' && emp.boss) return emp.boss
            if (!emp || !emp.email) return ''

            const userKey = emp.email.replace(/\./g, '||')
            const userRecord = allUsersByKey[userKey]
            return userRecord && typeof userRecord.boss === 'string' ? userRecord.boss : ''
          }

          const holidayDates = new Set<string>()
          if (holidaySnap) {
            holidaySnap.forEach((doc: any) => {
              const dateKey = normalizeDateKey(doc.data().date)
              if (dateKey && dateKey >= past90 && dateKey <= currentDate) {
                holidayDates.add(dateKey)
              }
            })
          }

          for (let i = 0; i < employees.length; i++) {
            const emp = employees[i]
            if (!emp.email) continue

            const emailScores: any = {}
            const dailyAnswers = dailyAnswerColorByEmail[emp.email] || {}
            const dailyAnswerDates = Object.keys(dailyAnswers).sort()

            emailScores.computed_flight_risk = calculateFlightRiskScore(dailyAnswers, currentDate, emp, holidayDates)

            const workDate = normalizeDateKey(emp.workdate)
            const consistencyStart = workDate && workDate > past90 ? workDate : past90
            const expectedCheckins = countExpectedWorkdays(consistencyStart, currentDate, holidayDates)
            const completedCheckins = countAnsweredCheckinDates(
              dailyAnswerDates,
              consistencyStart,
              currentDate,
              holidayDates
            )
            emailScores.computed_checkin_consistency = expectedCheckins > 0
              ? clampPercent((completedCheckins * 100) / expectedCheckins)
              : 0

            const managerEmail = getManagerEmailForEmployee(emp)
            emailScores.computed_manager_responsiveness = managerEmail && managerResponsivenessByEmail[managerEmail] !== undefined
              ? managerResponsivenessByEmail[managerEmail]
              : 0

            scores[emp.email] = emailScores
          }

          commit('setCompositeScores', {
            scores,
            company: user.company,
            scope: 'detail',
            merge: true,
          })
        } finally {
          commit('setLoading', false)
          delete peopleDetailScorePromises[user.company]
        }
      })()

      return peopleDetailScorePromises[user.company]
    },

    async loadPersonDailyCheckinConsistency({ rootState, dispatch, commit }: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      const email = payload && payload.email ? payload.email : ''
      if (!user || !user.company || !email) return

      const promiseKey = `${user.company}|${email}`
      if (personDailyCheckinPromises[promiseKey]) {
        return personDailyCheckinPromises[promiseKey]
      }

      personDailyCheckinPromises[promiseKey] = (async () => {
        try {
          const timezone = await getCompanyTimezone(user.company)
          const now = toCompanyDate(new Date(), timezone)
          const currentDate = format(now, 'YYYY-MM-DD')
          const past90 = format(subDays(now, 90), 'YYYY-MM-DD')

          let peopleState = rootState.people || {}
          let employees = listMatchesCompany(peopleState.starperformall, user.company)
            ? peopleState.starperformall.filter((item: any) => !!item)
            : (listMatchesCompany(peopleState.membersall, user.company)
              ? peopleState.membersall.filter((item: any) => !!item)
              : [])
          let employee = employees.find((item: any) => item && item.email === email)

          if (!employee) {
            await dispatch('people/getStarAndRiskPerform', user, { root: true })
            peopleState = rootState.people || {}
            employees = listMatchesCompany(peopleState.starperformall, user.company)
              ? peopleState.starperformall.filter((item: any) => !!item)
              : (listMatchesCompany(peopleState.membersall, user.company)
                ? peopleState.membersall.filter((item: any) => !!item)
                : [])
            employee = employees.find((item: any) => item && item.email === email)
          }

          if (!employee) return

          const [
            dailyQuestionSnap,
            holidaySnap,
          ] = await Promise.all([
            firebase.firestore().collection('users_questions')
              .where('users', '==', email)
              .where('quest_id', '==', HAPPINESS_QUESTION_ID)
              .where('date', '>=', past90)
              .where('date', '<=', currentDate)
              .get().catch(e => { console.warn('Failed to load person daily check-in questions:', e); return null }),
            firebase.firestore().collection('holliday')
              .where('companyid', '==', user.company)
              .get().catch(e => { console.warn('Failed to load holidays:', e); return null }),
          ])

          const dailyAnswerColorByEmail: Record<string, Record<string, string>> = {}
          const dailyExpectedDatesByEmail: Record<string, Set<string>> = {}
          ingestDailyCheckinQuestions(
            dailyQuestionSnap,
            dailyAnswerColorByEmail,
            dailyExpectedDatesByEmail,
            past90,
            currentDate
          )

          const holidayDates = new Set<string>()
          if (holidaySnap) {
            holidaySnap.forEach((doc: any) => {
              const dateKey = normalizeDateKey(doc.data().date)
              if (dateKey && dateKey >= past90 && dateKey <= currentDate) {
                holidayDates.add(dateKey)
              }
            })
          }

          const dailyAnswers = dailyAnswerColorByEmail[email] || {}
          const dailyAnswerDates = Object.keys(dailyAnswers).sort()
          const workDate = normalizeDateKey(employee.workdate)
          const consistencyStart = workDate && workDate > past90 ? workDate : past90
          const expectedCheckinDates = dailyExpectedDatesByEmail[email]
          const useIssuedCheckinDates = expectedCheckinDates && expectedCheckinDates.size > 0
          const expectedCheckins = useIssuedCheckinDates
            ? countDatesInRange(expectedCheckinDates, consistencyStart, currentDate)
            : countExpectedWorkdays(consistencyStart, currentDate, holidayDates)
          const completedCheckins = countAnsweredCheckinDates(
            dailyAnswerDates,
            consistencyStart,
            currentDate,
            holidayDates,
            useIssuedCheckinDates ? expectedCheckinDates : undefined
          )
          const personScores: any = {}

          if (dailyQuestionSnap) {
            personScores.computed_flight_risk = calculateFlightRiskScore(dailyAnswers, currentDate, employee, holidayDates)
          }

          if (dailyQuestionSnap && !dailyQuestionSnap.empty) {
            personScores.computed_checkin_consistency = expectedCheckins > 0
              ? clampPercent((completedCheckins * 100) / expectedCheckins)
              : 0
          }

          if (Object.keys(personScores).length === 0) return

          commit('setCompositeScores', {
            scores: {
              [email]: personScores,
            },
            company: user.company,
            scope: 'person-detail',
            merge: true,
          })
        } finally {
          delete personDailyCheckinPromises[promiseKey]
        }
      })()

      return personDailyCheckinPromises[promiseKey]
    },

    async loadCompositeScores({ state, rootState, dispatch, commit }: any, payload: any) {
      const user = payload && payload.user ? payload.user : payload
      const includeWho5 = !!(payload && payload.includeWho5)
      const includeDailyCheckins = !!(payload && payload.includeDailyCheckins)
      if (!user || !user.company) return

      const now = payload && payload.now
        ? new Date(payload.now)
        : toCompanyDate(new Date(), await getCompanyTimezone(user.company))
      const requestedWho5CacheKey = getQuarterCacheKey(user.company, now)

      if (
        state.compositeLoaded &&
        state.compositeCompany === user.company &&
        (!includeWho5 || (
          state.who5Loaded &&
          state.who5ScoresCompany === user.company &&
          state.who5ScoresCacheKey === requestedWho5CacheKey
        )) &&
        (!includeDailyCheckins || state.dailyCheckinsLoaded)
      ) {
        return
      }

      commit('setLoading', true)
      try {
        const scores: Record<string, any> = {}
        const currentDate = format(now, 'YYYY-MM-DD')
        const past90 = format(subDays(now, 90), 'YYYY-MM-DD')
        const past180 = format(subDays(now, 180), 'YYYY-MM-DD')
        let peopleState = rootState.people || {}
        const hasStarPerform = listMatchesCompany(peopleState.starperformall, user.company)
        if (!hasStarPerform) {
          await dispatch('people/getStarAndRiskPerform', user, { root: true })
        }

        peopleState = rootState.people || {}
        const employees = listMatchesCompany(peopleState.starperformall, user.company)
          ? peopleState.starperformall.filter((item: any) => !!item)
          : (listMatchesCompany(peopleState.membersall, user.company)
            ? peopleState.membersall.filter((item: any) => !!item)
            : [])

        if (employees.length === 0) {
          return
        }

        let allUsersByKey = (rootState.main && rootState.main.allUser) ? rootState.main.allUser : {}
        if (!allUsersByKey || Object.keys(allUsersByKey).length === 0) {
          await dispatch('main/getuser', false, { root: true })
          allUsersByKey = (rootState.main && rootState.main.allUser) ? rootState.main.allUser : {}
        }

        // --- Build month keys for report_monthly ---
        const monthKeys: string[] = []
        for (let i = 0; i < 10; i++) {
          monthKeys.push(format(subMonths(now, i), 'YYYY-MM'))
        }
        const uniqueMonths = monthKeys.filter((value, index, arr) => arr.indexOf(value) === index)
        const dailyCheckinPromise = includeDailyCheckins
          ? firebase.firestore().collection('users_questions')
            .where('company', '==', user.company)
            .where('quest_id', '==', HAPPINESS_QUESTION_ID)
            .where('date', '>=', past90)
            .where('date', '<=', currentDate)
            .get().catch(e => { console.warn('Failed to load daily check-in questions:', e); return null })
          : Promise.resolve(null)
        const who5ScorePromise = includeWho5
          ? loadWho5ScoreBundle(user.company, now)
          : Promise.resolve(null)
        const engagementPromise = loadPeopleEngagementInputs(user.company, now)

        // --- Fire all Firestore queries in parallel ---
        const firestorePromises: Array<Promise<any>> = [
          firebase.firestore().collection('report_monthly')
            .where('company', '==', user.company)
            .where('monthyear', 'in', uniqueMonths)
            .get().catch(e => { console.warn('Failed to load report_monthly:', e); return null }),
          firebase.firestore().collection('insight_manager_today')
            .where('company', '==', user.company)
            .where('date', '>=', format(subDays(now, 30), 'YYYY-MM-DD'))
            .where('date', '<=', currentDate)
            .orderBy('date')
            .get().catch(e => { console.warn('Failed to load manager responsiveness data:', e); return null }),
          firebase.firestore().collection('focus_summary_latest')
            .where('company', '==', user.company)
            .get().catch(e => { console.warn('Failed to load focus data:', e); return null }),
          firebase.firestore().collection('send_review_gem')
            .where('company', '==', user.company)
            .where('createdate', '>=', past180)
            .get().catch(e => { console.warn('Failed to load performance data:', e); return null }),
          firebase.firestore().collection('here_point')
            .where('company', '==', user.company)
            .where('date', '>=', past180)
            .where('statusrow', 'in', ['sendcoin'])
            .get().catch(e => { console.warn('Failed to load recognition data:', e); return null }),
          firebase.firestore().collection('peerfeedback')
            .where('company', '==', user.company)
            .where('date', '>=', past180)
            .get().catch(e => { console.warn('Failed to load peerfeedback data:', e); return null }),
          firebase.firestore().collection('holliday')
            .where('companyid', '==', user.company)
            .get().catch(e => { console.warn('Failed to load holidays:', e); return null }),
          dailyCheckinPromise,
          who5ScorePromise,
        ]

        const [
          monthlySnap,
          managerInsightSnap,
          focusSnap,
          reviewSnap,
          hereSnap,
          feedbackSnap,
          holidaySnap,
          dailyQuestionSnap,
          who5Bundle,
          engagementResult,
        ] = await Promise.all(firestorePromises.concat([engagementPromise]))

        // --- Process report_monthly for flight risk + engagement ---
        const dailyAnswerColorByEmail: Record<string, Record<string, string>> = {}
        const dailyExpectedDatesByEmail: Record<string, Set<string>> = {}
        const engagementByEmail: Record<string, any> = {}
        const managerFeedbackTotalsByEmail: Record<string, { read: number, receive: number }> = {}
        const setDailyAnswer = (email: string, dateKey: string, color: string) => {
          setDailyAnswerColor(dailyAnswerColorByEmail, email, dateKey, color, past90, currentDate)
        }
        if (monthlySnap) {
          monthlySnap.forEach((doc: any) => {
            const data = doc.data()
            if (!data.email) return

            if (data.daily_answer) {
              const answers = data.daily_answer
              for (const date in answers) {
                const color = answers[date]
                setDailyAnswer(data.email, date, color)
              }
            }

            if (!engagementByEmail[data.email] || data.monthyear > engagementByEmail[data.email]._monthyear) {
              engagementByEmail[data.email] = {
                _monthyear: data.monthyear,
                daily_color: data.daily_color || '',
                daily: data.daily,
                feedback_color: data.feedback_color || '',
                feedback: data.feedback,
                super_feedback_color: data.super_feedback_color || '',
                super_feedback: data.super_feedback,
                recognition_color: data.recognition_color || '',
                recognition: data.recognition,
                super_recognition_color: data.super_recognition_color || '',
                super_recognition: data.super_recognition,
                peerfeedback_color: data.peerfeedback_color || '',
                peerfeedback: data.peerfeedback,
                happily_village_color: data.happily_village_color || '',
                favorite_spark_color: data.favorite_spark_color || '',
                replyrate: clampPercent(parseFloat(data.replyrate)),
              }
            }
          })
        }

        ingestDailyCheckinQuestions(
          dailyQuestionSnap,
          dailyAnswerColorByEmail,
          dailyExpectedDatesByEmail,
          past90,
          currentDate
        )

        if (managerInsightSnap) {
          managerInsightSnap.forEach((doc: any) => {
            const data = doc.data()
            if (!data.email || data.type !== 'feedback') return

            const receive = parseAnswerValue(data.receive)
            const rawRead = parseAnswerValue(data.read)
            if (receive === null || rawRead === null) return

            const read = rawRead > receive ? receive : rawRead
            if (!managerFeedbackTotalsByEmail[data.email]) {
              managerFeedbackTotalsByEmail[data.email] = { read: 0, receive: 0 }
            }

            managerFeedbackTotalsByEmail[data.email].read += read
            managerFeedbackTotalsByEmail[data.email].receive += receive
          })
        }

        const managerResponsivenessByEmail: Record<string, number> = {}
        Object.keys(managerFeedbackTotalsByEmail).forEach((email) => {
          const totals = managerFeedbackTotalsByEmail[email]
          managerResponsivenessByEmail[email] = totals.receive > 0
            ? clampPercent((totals.read * 100) / totals.receive)
            : 0
        })

        const getManagerEmailForEmployee = (emp: any): string => {
          if (emp && typeof emp.boss === 'string' && emp.boss) return emp.boss
          if (!emp || !emp.email) return ''

          const userKey = emp.email.replace(/\./g, '||')
          const userRecord = allUsersByKey[userKey]
          return userRecord && typeof userRecord.boss === 'string' ? userRecord.boss : ''
        }

        const who5ByEmail = includeWho5 && who5Bundle ? who5Bundle.scores : {}
        const hasDebiEngagementRecords = !!(engagementResult && Array.isArray(engagementResult.debiRecords))
        const debiEngagementByEmail = hasDebiEngagementRecords
          ? buildIndividualDebiEngagementScoreDetails(engagementResult.debiRecords || [])
          : {}

        // --- Build a holiday set for fallback expected-day calculations ---
        const holidayDates = new Set<string>()
        if (holidaySnap) {
          holidaySnap.forEach((doc: any) => {
            const dateKey = normalizeDateKey(doc.data().date)
            if (dateKey && dateKey >= past90 && dateKey <= currentDate) {
              holidayDates.add(dateKey)
            }
          })
        }

        // --- Process focus_summary_latest ---
        const focusByEmail: Record<string, { total: number, done: number, aligned: number }> = {}
        if (focusSnap) {
          focusSnap.forEach((doc: any) => {
          const data = doc.data()
          const email = doc.id
          if (!data.checklist) return
          let checklistArray: any[] = []
          const checklist = data.checklist
          if (Array.isArray(checklist)) {
            checklistArray = checklist
          } else if (typeof checklist === 'object' && checklist !== null) {
            checklistArray = Object.values(checklist)
          }
          const items = checklistArray.filter((item: any) => item && item.item)
          if (items.length === 0) return
          const total = items.length
          const done = items.filter((item: any) => item.status === 'done').length
          const aligned = items.filter((item: any) => item.goal_id && item.goal_id !== 'unmatched').length
          focusByEmail[email] = { total, done, aligned }
          })
        }

        // --- Process send_review_gem for gem rate + exceeding rate ---
        // `gems` = culture+goal both 3 or 4 (Meeting OR Exceeding) — drives computed_performance.
        // `exceedings` = culture+goal both 4 (Exceeding only) — drives computed_exceeding_performance,
        // the promotion-readiness signal. Meeting expectations is a fine performance score but not
        // a promotion signal on its own; consistent Exceeding is.
        const perfByEmail: Record<string, { gems: number, exceedings: number, total: number }> = {}
        if (reviewSnap) {
          reviewSnap.forEach((doc: any) => {
          const data = doc.data()
          if (!data.receiver || isNaN(data.culture) || isNaN(data.goal)) return
          const email = data.receiver
          if (!perfByEmail[email]) perfByEmail[email] = { gems: 0, exceedings: 0, total: 0 }
          perfByEmail[email].total++
          if ((data.culture == 3 || data.culture == 4) && (data.goal == 3 || data.goal == 4)) {
            perfByEmail[email].gems++
          }
          if (data.culture == 4 && data.goal == 4) {
            perfByEmail[email].exceedings++
          }
          })
        }

        // --- Process here_point for recognition counts, alignment quality, and unique pairs ---
        const recognitionByEmail: Record<string, { given: number, received: number }> = {}
        const alignmentByEmail: Record<string, { effortReceived: number, totalReceived: number }> = {}
        // Track unique recognition partners per person (for reciprocity count)
        const recognitionSentTo: Record<string, Set<string>> = {}
        const recognitionReceivedFrom: Record<string, Set<string>> = {}
        if (hereSnap) {
          hereSnap.forEach((doc: any) => {
          const data = doc.data()
          const sender = data.sender_email
          const receiver = data.send_to_email
          if (sender) {
            if (!recognitionByEmail[sender]) recognitionByEmail[sender] = { given: 0, received: 0 }
            recognitionByEmail[sender].given++
            if (receiver) {
              if (!recognitionSentTo[sender]) recognitionSentTo[sender] = new Set()
              recognitionSentTo[sender].add(receiver)
            }
          }
          if (receiver) {
            if (!recognitionByEmail[receiver]) recognitionByEmail[receiver] = { given: 0, received: 0 }
            recognitionByEmail[receiver].received++
            if (sender) {
              if (!recognitionReceivedFrom[receiver]) recognitionReceivedFrom[receiver] = new Set()
              recognitionReceivedFrom[receiver].add(sender)
            }
          }
          if (data.core_value_ai && receiver) {
            let maxRating = 0
            for (const key in data.core_value_ai) {
              const r = parseFloat(data.core_value_ai[key].rating)
              if (r > maxRating) maxRating = r
            }
            if (!alignmentByEmail[receiver]) alignmentByEmail[receiver] = { effortReceived: 0, totalReceived: 0 }
            alignmentByEmail[receiver].effortReceived += maxRating
            alignmentByEmail[receiver].totalReceived++
          }
          })
        }

        // --- Process peerfeedback for unique peer counts + Truly/Exceeding received ---
        const feedbackGivenByEmail: Record<string, number> = {}
        const trulyReceivedByEmail: Record<string, number> = {}
        // Track unique peers for peer trust count
        const feedbackSentTo: Record<string, Set<string>> = {}
        const feedbackReceivedFrom: Record<string, Set<string>> = {}
        if (feedbackSnap) {
          feedbackSnap.forEach((doc: any) => {
          const data = doc.data()
          const sender = data.email
          const receiver = data.email_to
          // Count total feedback given (for trust score)
          if (sender) {
            feedbackGivenByEmail[sender] = (feedbackGivenByEmail[sender] || 0) + 1
            // Track unique peers sent to
            if (receiver) {
              if (!feedbackSentTo[sender]) feedbackSentTo[sender] = new Set()
              feedbackSentTo[sender].add(receiver)
            }
          }
          // Track unique peers received from
          if (receiver) {
            if (!feedbackReceivedFrom[receiver]) feedbackReceivedFrom[receiver] = new Set()
            if (sender) feedbackReceivedFrom[receiver].add(sender)
          }
          // Count Truly/Exceeding feedback received
          if (receiver && data.suggest && data.suggest.line1_en) {
            const rating = data.suggest.line1_en
            if (rating === 'Truly' || rating === 'Exceeding') {
              trulyReceivedByEmail[receiver] = (trulyReceivedByEmail[receiver] || 0) + 1
            }
          }
          })
        }

        // --- Compute scores for all employees ---
        const colorScore = (c: string) => {
          if (c === 'green') return 1.0
          if (c === 'yellow') return 0.5
          return 0
        }

        const allGivenCounts: number[] = []
        const allReceivedCounts: number[] = []
        for (let i = 0; i < employees.length; i++) {
          const emp = employees[i]
          if (!emp.email) continue
          const emailScores: any = {}

        // Flight Risk Score (0-100, higher = more risk)
        // Unhappy check-in patterns are the primary signal. Low well-being and
        // weak connection signals can only lift weak/noisy patterns into watch,
        // never into high risk on their own.
        const dailyAnswers = dailyAnswerColorByEmail[emp.email] || {}
        const dailyAnswerDates = Object.keys(dailyAnswers).sort()
        emailScores.computed_flight_risk = calculateFlightRiskScore(dailyAnswers, currentDate, emp, holidayDates)

        // Engagement Score (0-100, higher = better). Prefer the individual
        // daily DEBI components; use the monthly proxy only when daily DEBI
        // records are unavailable.
        if (hasDebiEngagementRecords) {
          if (debiEngagementByEmail[emp.email]) {
            Object.assign(emailScores, debiEngagementByEmail[emp.email])
          } else {
            emailScores.computed_engagement = null
            emailScores.computed_engagement_source = 'debi'
            emailScores.computed_engagement_period = ''
            emailScores.computed_engagement_breakdown = null
          }
        } else {
          const engData = engagementByEmail[emp.email]
          if (engData) {
            const detail = buildMonthlyEngagementScoreDetail(engData)
            if (detail) {
              Object.assign(emailScores, detail)
            } else {
              emailScores.computed_engagement = null
            }
          } else {
            emailScores.computed_engagement = null
          }
        }

        if (includeWho5) {
          emailScores.computed_who5 = who5ByEmail[emp.email] !== undefined ? who5ByEmail[emp.email] : null
        } else {
          const existingWho5 = state.compositeScores && state.compositeScores[emp.email]
            ? state.compositeScores[emp.email].computed_who5
            : undefined
          if (existingWho5 !== undefined) {
            emailScores.computed_who5 = existingWho5
          }
        }

        const workDate = normalizeDateKey(emp.workdate)
        const consistencyStart = workDate && workDate > past90 ? workDate : past90
        const expectedCheckinDates = dailyExpectedDatesByEmail[emp.email]
        const useIssuedCheckinDates = expectedCheckinDates && expectedCheckinDates.size > 0
        const expectedCheckins = useIssuedCheckinDates
          ? countDatesInRange(expectedCheckinDates, consistencyStart, currentDate)
          : countExpectedWorkdays(consistencyStart, currentDate, holidayDates)
        const completedCheckins = countAnsweredCheckinDates(
          dailyAnswerDates,
          consistencyStart,
          currentDate,
          holidayDates,
          useIssuedCheckinDates ? expectedCheckinDates : undefined
        )
        emailScores.computed_checkin_consistency = expectedCheckins > 0
          ? clampPercent((completedCheckins * 100) / expectedCheckins)
          : 0

        const managerEmail = getManagerEmailForEmployee(emp)
        emailScores.computed_manager_responsiveness = managerEmail && managerResponsivenessByEmail[managerEmail] !== undefined
          ? managerResponsivenessByEmail[managerEmail]
          : 0

        // Focus Score (0-100): number of focus items, 5 items = 100
        const focusData = focusByEmail[emp.email]
        const focusCount = focusData ? focusData.total : 0
        emailScores.computed_focus = Math.round(Math.min(focusCount / 5, 1) * 1000) / 10

        // Goal Alignment (0-100, higher = better)
        if (focusData && focusData.total > 0) {
          emailScores.computed_goal_alignment = Math.round((focusData.aligned / focusData.total) * 1000) / 10
        } else {
          emailScores.computed_goal_alignment = 0
        }

        // Performance Score (0-100) — gem rate × confidence
        // sqrt confidence curve: 1 boost ≈ 41% credit, 3 ≈ 71%, 6+ = full credit
        // Null when no manager review records exist for this person — distinguishes
        // "no performance feedback received" from "received feedback that scored 0".
        // Downstream consumers that use `|| 0` fallback continue to work; consumers
        // that need the no-data signal can check for null explicitly.
        //
        // computed_exceeding_performance follows the same curve but only counts reviews
        // where culture AND goal are both 4 (Exceeding). This is the promotion-readiness
        // signal — Meeting expectations isn't sufficient evidence of being ready for the
        // next level. Same null convention for "no manager reviews."
        const perfData = perfByEmail[emp.email]
        if (perfData && perfData.total > 0) {
          const confidence = Math.min(Math.sqrt(perfData.total / 6), 1.0)
          const gemRate = perfData.gems / perfData.total
          const exceedingRate = perfData.exceedings / perfData.total
          emailScores.computed_performance = Math.round(gemRate * confidence * 1000) / 10
          emailScores.computed_exceeding_performance = Math.round(exceedingRate * confidence * 1000) / 10
        } else {
          emailScores.computed_performance = null
          emailScores.computed_exceeding_performance = null
        }

        // Peer Trust Count: unique feedback peers + reciprocal recognition pairs
        const fbSent = feedbackSentTo[emp.email] ? feedbackSentTo[emp.email].size : 0
        const fbReceived = feedbackReceivedFrom[emp.email] ? feedbackReceivedFrom[emp.email].size : 0
        let reciprocalPairs = 0
        const sentTo = recognitionSentTo[emp.email]
        const recvFrom = recognitionReceivedFrom[emp.email]
        if (sentTo && recvFrom) {
          sentTo.forEach(function (peer) { if (recvFrom.has(peer)) reciprocalPairs++ })
        }
        emailScores.computed_peer_trust = fbSent + fbReceived + reciprocalPairs

        // Trust Score + recognition — computed in second pass after median normalization

        // Collect recognition counts for median calculation
        const recData = recognitionByEmail[emp.email]
        allGivenCounts.push(recData ? (recData.given || 0) : 0)
        allReceivedCounts.push(recData ? (recData.received || 0) : 0)

          scores[emp.email] = emailScores
        }

        // --- Median-based normalization for recognition + trust ---

        const getMedian = (arr: number[]): number => {
          if (arr.length === 0) return 1
          const nonZero = arr.filter(v => v > 0).sort((a, b) => a - b)
          if (nonZero.length === 0) return 1
          const mid = Math.floor(nonZero.length / 2)
          return nonZero.length % 2 === 0
            ? (nonZero[mid - 1] + nonZero[mid]) / 2
            : nonZero[mid]
        }

        const medianGiven = getMedian(allGivenCounts)
        const medianReceived = getMedian(allReceivedCounts)

        for (let i = 0; i < employees.length; i++) {
          const emp = employees[i]
          if (!emp.email || !scores[emp.email]) continue
          const recData = recognitionByEmail[emp.email]
          const given = recData ? (recData.given || 0) : 0
          const received = recData ? (recData.received || 0) : 0

        scores[emp.email].computed_giving_recognition =
          Math.round(Math.min(given / (2 * medianGiven), 1) * 1000) / 10
        scores[emp.email].computed_receiving_recognition =
          Math.round(Math.min(received / (2 * medianReceived), 1) * 1000) / 10

        // Trust Score (0-100, higher = better)
        // 1. Peer Feedback Activity (30%) — giving + receiving strong ratings
        //    Use weighted blend: stronger signal drives 70%, weaker adds 30% bonus
        const feedbackGiven = feedbackGivenByEmail[emp.email] || 0
        const trulyCount = trulyReceivedByEmail[emp.email] || 0
        const feedbackGivenNorm = Math.min(feedbackGiven / 5, 1.0)
        const trulyNorm = Math.min(trulyCount / 5, 1.0)
        const peerHigh = Math.max(feedbackGivenNorm, trulyNorm)
        const peerLow = Math.min(feedbackGivenNorm, trulyNorm)
        const peerFeedbackActivity = peerHigh * 0.7 + peerLow * 0.3

        // 2. Recognition Reciprocity (30%) — balanced give/receive
        //    Same blend: stronger signal drives 70%, weaker adds 30% bonus
        const givenNorm = Math.min(given / (2 * medianGiven), 1.0)
        const receivedNorm = Math.min(received / (2 * medianReceived), 1.0)
        const recHigh = Math.max(givenNorm, receivedNorm)
        const recLow = Math.min(givenNorm, receivedNorm)
        const recognitionReciprocity = recHigh * 0.7 + recLow * 0.3

        // 3. Network Strength (20%) — from emp.network_type
        let networkStrength = 0.1
        const netType = (emp.network_type || '').toLowerCase()
        if (netType.indexOf('super') >= 0) networkStrength = 1.0
        else if (netType.indexOf('high') >= 0) networkStrength = 0.7
        else if (netType.indexOf('medium') >= 0) networkStrength = 0.4
        else if (netType.indexOf('low') >= 0) networkStrength = 0.1

        // 4. Recognition Quality Received (20%) — average core_value_ai rating from here_point
        const alignInfo = alignmentByEmail[emp.email]
        const alignmentReceived = (alignInfo && alignInfo.totalReceived > 0)
          ? alignInfo.effortReceived / alignInfo.totalReceived : 0
        const recognitionQuality = Math.min(alignmentReceived / 5, 1.0)

          scores[emp.email].computed_trust = Math.round(
            (peerFeedbackActivity * 0.30 + recognitionReciprocity * 0.30 + networkStrength * 0.20 + recognitionQuality * 0.20) * 1000
          ) / 10
        }

        commit('setCompositeScores', {
          scores,
          company: user.company,
          who5Loaded: includeWho5 || (state.who5Loaded && state.who5ScoresCompany === user.company),
          who5CacheKey: includeWho5 && who5Bundle ? who5Bundle.cacheKey : state.who5ScoresCacheKey,
          dailyCheckinsLoaded: (includeDailyCheckins && !!dailyQuestionSnap) || state.dailyCheckinsLoaded,
        })
      } finally {
        commit('setLoading', false)
      }
    },
  },
}
